/**
 * EcoSealPro Variant Picker
 * A clean, modular implementation of the variant picker for EcoSealPro products
 */

(function() {
  'use strict';
  
  /**
   * In quick-view, the same picker markup can exist on the page AND inside an opened quick-view drawer.
   * Prefer scoping DOM queries to the currently opened quick-view drawer (if present) to avoid
   * targeting the wrong instance.
   */
  const getScopeRoot = function() {
    return (
      document.querySelector('quick-view-drawer details[open]') ||
      document.querySelector('quick-view-drawer details.menu-opening') ||
      document.querySelector('.quick-view--open') ||
      document.querySelector('.quick-view--opening') ||
      document
    );
  };

  // DOM Helper utilities
  const DOMHelper = {
    /**
     * Safely get an element from the DOM
     * @param {string} selector - CSS selector
     * @param {Element|Document|null} root - Optional root element to scope the query
     * @return {Element|null} - DOM element or null if not found
     */
    getElement: function(selector, root = null) {
      const scope = root || getScopeRoot();
      return scope.querySelector(selector) || null;
    },
    
    /**
     * Get multiple elements from the DOM
     * @param {string} selector - CSS selector
     * @param {Element|Document|null} root - Optional root element to scope the query
     * @return {NodeList} - Collection of DOM elements
     */
    getElements: function(selector, root = null) {
      const scope = root || getScopeRoot();
      return scope.querySelectorAll(selector);
    },
    
    /**
     * Create a new DOM element with optional attributes and classes
     * @param {string} tagName - HTML tag name
     * @param {object} options - Element attributes and properties
     * @return {Element} - The created DOM element
     */
    createElement: function(tagName, options = {}) {
      const customizedIs = options.is || (options.attributes && options.attributes.is);
      let element;
      if (customizedIs) {
        try {
          element = document.createElement(tagName, { is: customizedIs });
        } catch (e) {
          element = document.createElement(tagName);
        }
      } else {
        element = document.createElement(tagName);
      }
      
      if (options.className) {
        element.className = options.className;
      }
      
      if (options.attributes) {
        Object.entries(options.attributes).forEach(([key, value]) => {
          if (key === 'is') return;
          element.setAttribute(key, value);
        });
      }
      
      // Best-effort: preserve the `is="..."` attribute even if customized built-ins aren't supported.
      if (customizedIs && !element.getAttribute('is')) {
        try { element.setAttribute('is', customizedIs); } catch (e) { /* no-op */ }
      }
      
      if (options.text) {
        element.textContent = options.text;
      }
      
      if (options.html) {
        element.innerHTML = options.html;
      }
      
      if (options.styles) {
        Object.entries(options.styles).forEach(([prop, value]) => {
          element.style[prop] = value;
        });
      }
      
      if (options.children) {
        options.children.forEach(child => {
          element.appendChild(child);
        });
      }
      
      return element;
    }
  };

  // Configuration and constants
  const Config = {
    // Default settings
    defaults: {
      initialJarType: 'S',
      groupCount: 1
    },

    translationFallbacks: {
      twistSmall: 'Twist-off S',
      twistMedium: 'Twist-off M',
      twistLarge: 'Twist-off L',
      twistExtraSmall: 'Twist-off XS',
      masonSmall: 'Ball Mason S',
      masonMedium: 'Ball Mason M',
      masonLarge: 'Ball Mason L',
      yourSelection: 'Your custom set',
      configureTrio: 'Configure Trio',
      ecosealpro: 'EcoSeal Pro',
      twistOffGlass: 'Twist-off',
      masonJars: 'Ball Mason',
      clickToConfigure: 'Configure',
      soldOut: 'Sold out',
      rabattometerTitle: 'Discount Meter',
      jarsLabel: 'Jars',
      lidsLabel: 'Lids',
      nextDiscountHint: 'Choose {jarsNeeded} jars to get {discount}% off!',
      nextLidDiscountHint: 'Choose {lidsNeeded} lids to get {discount}% off!',
      maxDiscountHint: 'Maximum discount unlocked!',
      selectJarsHint: 'Select jars to continue.',
      selectLidsHint: 'Select lids to continue.',
      discountProgress: 'Discount progress',
      checkout: 'Checkout',
      yourAnySealSelection: 'Your AnySeal set',
      anySealStarterTitle: 'AnySeal starter',
      anySealStarterDescription: 'incl. AnySeal adapter',
      anySealAdapterTitle: 'AnySeal adapter',
      anySealAddonTitle: 'AnySeal lid 3-pack',
      anySealLidsTitle: 'AnySeal lids',
      anySealAddonDescription: 'Add more lids with the same vacuum workflow.',
      anySealSupportEyebrow: 'Bundle discount',
      anySealSupportText: 'Discounts apply only to extra 3-packs. The 3 lids in the starter stay full price.',
      chooseWorldFirst: 'Choose first how you want to vacuum seal.'
    },
    
    // Translations
    translations: {
      missingWarnings: new Set(),

      isMissingValue: function(value, key) {
        if (value == null || value === '') return true;
        if (value === key) return true;
        if (typeof value !== 'string') return false;

        return /^(ecosealpro-|products\.|sections\.)/i.test(value) || /^[a-z0-9_-]+\.[a-z0-9_.-]+$/i.test(value);
      },

      get: function(key) {
        const translation = window.ecosealTranslations && window.ecosealTranslations[key];
        if (!this.isMissingValue(translation, key)) {
          return translation;
        }

        const fallback = Config.translationFallbacks[key];
        if (fallback !== undefined) {
          return fallback;
        }

        if (!this.missingWarnings.has(key)) {
          this.missingWarnings.add(key);
          console.warn(`Translation key '${key}' not found`);
        }

        return key;
      }
    },
    
    // Asset URLs
    assets: {
      emptyImage:
        'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=',

      getJarMaps: function(size) {
        const assets = window.ecosealAssets || {};
        const requested = (size || '').toLowerCase();
        const preferredMap =
          (requested === 'small' && assets.jarImagesSmall) ||
          (requested === 'modal' && assets.jarImagesModal) ||
          assets.jarImages ||
          assets.jarImagesModal ||
          assets.jarImagesSmall;

        return [
          preferredMap,
          assets.jarImages,
          assets.jarImagesModal,
          assets.jarImagesSmall
        ].filter(Boolean);
      },

      getJarImage: function(jarCode, size) {
        const maps = this.getJarMaps(size);

        for (let i = 0; i < maps.length; i += 1) {
          const map = maps[i];
          if (map && map[jarCode]) {
            return map[jarCode];
          }
        }

        for (let i = 0; i < maps.length; i += 1) {
          const firstValue = Object.values(maps[i] || {})[0];
          if (firstValue) {
            return firstValue;
          }
        }

        return this.emptyImage;
      },
      getSealerImage: function() {
        const assets = window.ecosealAssets || {};
        if (assets.sealerImage) {
          return assets.sealerImage;
        }
        if (assets.sealerImageSmall) {
          return assets.sealerImageSmall;
        }
        const firstJarImage = this.getJarImage('S', 'modal');
        return firstJarImage || this.emptyImage;
      },
      getSealerImageSmall: function() {
        const assets = window.ecosealAssets || {};
        if (assets.sealerImageSmall) return assets.sealerImageSmall;
        if (assets.sealerImage) return assets.sealerImage;
        const firstJarImage = this.getJarImage('S', 'small');
        return firstJarImage || this.emptyImage;
      },
      getAnySealStarterImage: function() {
        const assets = window.ecosealAssets || {};
        return assets.anySealStarterImage || assets.anySealWorldImage || this.getSealerImage();
      },
      getAnySealAdapterImage: function() {
        const assets = window.ecosealAssets || {};
        return assets.anySealAdapterImage || assets.anySealWorldImage || this.getSealerImageSmall();
      },
      getAnySealLidsImage: function() {
        const assets = window.ecosealAssets || {};
        return assets.anySealLidsImage || assets.anySealWorldImage || this.getJarImage('H', 'modal');
      },
      getWorldImage: function(world) {
        const assets = window.ecosealAssets || {};
        if (world === 'anyseal') {
          return assets.anySealWorldImage || this.getAnySealStarterImage();
        }
        if (world === 'sealit') {
          return assets.sealItWorldImage || this.getJarImage('S', 'modal');
        }
        return this.emptyImage;
      }
    }
  };

  // Variant manager for handling SKU generation and variant selection
  const VariantManager = {
    /**
     * Get the single EcoSeal option <select> that drives Shopify variant selection.
     * EcoSeal has only one option, so selecting a variant is simply selecting an <option> and firing one change event.
     */
    getSelectElement: function() {
      const root = getScopeRoot();
      return (
        root.querySelector('variant-selects[data-primary] select[id^="Option-"][name^="options["]') ||
        root.querySelector('select[id^="Option-"][name^="options["]') ||
        null
      );
    },
    
    /**
     * Find an <option> by SKU
     */
    findOptionBySku: function(selectElement, sku) {
      if (!selectElement) return null;
      const targetSku = (sku || '').trim();
      if (!targetSku) return null;
      try {
        return selectElement.querySelector(`option[data-variant-sku="${CSS.escape(targetSku)}"]`);
      } catch (_e) {
        // Fallback if CSS.escape is missing (older browsers)
        return Array.from(selectElement.options).find((opt) => (opt.dataset.variantSku || '').trim() === targetSku) || null;
      }
    },
    
    /**
     * Find an <option> by variant id
     */
    findOptionById: function(selectElement, id) {
      if (!selectElement) return null;
      if (id == null) return null;
      const targetId = String(id);
      try {
        return selectElement.querySelector(`option[data-variant-id="${CSS.escape(targetId)}"]`);
      } catch (_e) {
        return Array.from(selectElement.options).find((opt) => String(opt.dataset.variantId || '') === targetId) || null;
      }
    },
    
    /**
     * Find a variant by ID (minimal info; used for ?variant= init)
     */
    findById: function(id) {
      const selectElement = this.getSelectElement();
      const opt = this.findOptionById(selectElement, id);
      if (!opt || !opt.dataset.variantId) return null;
      return {
        id: Number(opt.dataset.variantId),
        sku: opt.dataset.variantSku || '',
        available: !opt.disabled,
        title: opt.textContent.trim(),
      };
    },

    syncVariantInputs: function(selectElement, variantId) {
      if (!selectElement || variantId == null) return;

      const productInfo = selectElement.closest('product-info');
      const scope = productInfo || getScopeRoot();
      scope.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = String(variantId);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },
    
    /**
     * Decode SKU back to variant configuration
     * @param {string} sku - The SKU to decode
     * @return {Object|null} - Object with quantity and selections, or null if invalid
     */
    decodeSku: function(sku) {
      // SKU format: _ECOPRO1SET{quantity}{jar codes}
      // Example: _ECOPRO1SET1K or _ECOPRO1SET3KMG
      if (!sku || !sku.startsWith('_ECOPRO1SET')) return null;
      
      // Extract the quantity and jar codes
      const quantityAndCodes = sku.substring('_ECOPRO1SET'.length);
      const quantity = parseInt(quantityAndCodes.charAt(0));
      
      if (isNaN(quantity) || quantity < 1 || quantity > 4) return null;
      
      // Map SKU letters back to internal jar codes
      const skuMap = {
        'K': 'S',    // TWIST_OFF_JAR_350ML
        'M': 'M',    // TWIST_OFF_JAR_770ML
        'G': 'L',    // TWIST_OFF_JAR_1062ML
        'A': 'MS',   // BALL_MASON_JAR_236ML
        'B': 'MM',   // BALL_MASON_JAR_473ML
        'C': 'ML',   // BALL_MASON_JAR_946ML
        'H': 'H'     // TWIST_OFF_JAR_230ML
      };
      
      // Get all jar codes from the SKU
      const jarCodes = quantityAndCodes.substring(1).split('');
      
      // Map back to internal codes
      const selections = jarCodes.map(code => skuMap[code] || null).filter(code => code !== null);
      
      // Validate that we have the right number of selections
      if (selections.length !== quantity) return null;
      
      return {
        quantity: quantity,
        selections: selections
      };
    },
    
    /**
     * Generate SKU based on jar selection
     * @param {string} quantity - The quantity of jars
     * @param {Array} sizes - Array of jar size codes
     * @return {string|null} - Generated SKU or null if invalid
     */
    generateSku: function(quantity, sizes) {
      // Map jar types to SKU letters
      const sizeMap = { 
        'TWIST_OFF_JAR_350ML': 'K', 
        'TWIST_OFF_JAR_770ML': 'M', 
        'TWIST_OFF_JAR_1062ML': 'G',
        'BALL_MASON_JAR_236ML': 'A',
        'BALL_MASON_JAR_473ML': 'B',
        'BALL_MASON_JAR_946ML': 'C',
        'TWIST_OFF_JAR_230ML': 'H' 
      };
      
      // Validate selection
      if (sizes.length !== parseInt(quantity)) return null;
      
      // Map jar types to SKU letters and sort
      const skuLetters = sizes.map(size => sizeMap[size]).sort();
      
      // Create final SKU
      return "_ECOPRO1SET" + quantity + skuLetters.join('');
    },
    
    /**
     * Update product variant based on current selection
     * @param {Array} selections - Current jar selections
     * @param {number} selectedGroups - Number of groups
     */
    updateVariantBySelection: function(selections, selectedGroups) {
      // Get all valid selections
      const validSelections = selections
        .slice(0, selectedGroups)
        .filter(selection => selection !== null);
      
      if (validSelections.length !== selectedGroups) return;

      const starterVariant = BundleManager.getStarterVariant(validSelections[0]);
      if (!starterVariant || !starterVariant.sku) return;

      this.updateVariantBySku(starterVariant.sku);
    },
    
    /**
     * Update product variant based on a direct SKU
     * @param {string} sku - The SKU of the variant to select
     */
    updateVariantBySku: function(sku) {
      // Match Milk-it's snappy UX: apply immediately (EcoSeal users expect instant price updates).
      this.applyVariantBySkuNow((sku || '').trim());
    },
    
    applyVariantBySkuNow: function(sku) {
      const selectElement = this.getSelectElement();
      if (!selectElement) return;

      const targetOption = this.findOptionBySku(selectElement, sku);

      if (!targetOption) {
        console.warn(`[VariantManager] No matching <option> for SKU: ${sku}`);
        return;
      }

      if (targetOption.dataset && targetOption.dataset.variantId) {
        this.syncVariantInputs(selectElement, targetOption.dataset.variantId);
      }

      // If already selected, stop after keeping the hidden variant inputs in sync.
      if (selectElement.value === targetOption.value) return;

      // Single change event => single publish(optionValueSelectionChange) => single product-info fetch.
      selectElement.value = targetOption.value;
      selectElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  // Jar data manager
  const JarData = {
    // Jar types with their properties
    jarTypes: {
      'S': { 
        label: Config.translations.get('twistSmall'),
        type: Config.translations.get('twistOffGlass'),
        size: 'S', 
        volume: '350ml',
        skuCode: 'TWIST_OFF_JAR_350ML'
      },
      'M': { 
        label: Config.translations.get('twistMedium'),
        type: Config.translations.get('twistOffGlass'),
        size: 'M', 
        volume: '770ml',
        skuCode: 'TWIST_OFF_JAR_770ML'
      },
      'L': { 
        label: Config.translations.get('twistLarge'),
        type: Config.translations.get('twistOffGlass'),
        size: 'L', 
        volume: '1062ml',
        skuCode: 'TWIST_OFF_JAR_1062ML'
      },
      'MS': { 
        label: Config.translations.get('masonSmall'),
        type: Config.translations.get('masonJars'),
        size: 'S', 
        volume: '236ml',
        skuCode: 'BALL_MASON_JAR_236ML'
      },
      'MM': { 
        label: Config.translations.get('masonMedium'),
        type: Config.translations.get('masonJars'),
        size: 'M', 
        volume: '473ml',
        skuCode: 'BALL_MASON_JAR_473ML'
      },
      'ML': { 
        label: Config.translations.get('masonLarge'),
        type: Config.translations.get('masonJars'),
        size: 'L', 
        volume: '946ml',
        skuCode: 'BALL_MASON_JAR_946ML'
      },
      'H': {
        label: Config.translations.get('twistExtraSmall'), 
        type: Config.translations.get('twistOffGlass'), 
        size: 'XS', 
        volume: '230ml', 
        skuCode: 'TWIST_OFF_JAR_230ML' 
      }
    },
    
    // Jar type groupings
    groups: {
      twistOff: ['S', 'M', 'L', 'H'],
      mason: ['MS', 'MM', 'ML']
    },
    
    // Get jar image URL
    getJarImageUrl: function(jarCode) {
      // Default to modal size unless a caller requests otherwise.
      return Config.assets.getJarImage(jarCode, 'modal');
    },
    
    getJarImageUrlSmall: function(jarCode) {
      return Config.assets.getJarImage(jarCode, 'small');
    },
    
    // Get sealer image URL
    getSealerImageUrl: function() {
      return Config.assets.getSealerImage();
    },
    
    getSealerImageUrlSmall: function() {
      return Config.assets.getSealerImageSmall();
    }
  };

  const BundleManager = {
    checkoutButtonClassName: 'shopify-payment-button__button shopify-payment-button__button--unbranded ecoseal-custom-checkout-button',

    getConfig: function() {
      return window.ecosealCartConfig || { starterVariants: {}, addOnVariants: {}, anySealStarterVariant: null, anySealAddOnVariant: null };
    },

    getProductInfo: function(source = null) {
      if (source && typeof source.closest === 'function') {
        const infoFromSource = source.closest('product-info');
        if (infoFromSource) {
          return infoFromSource.querySelector('.ecoseal-merged-picker') ? infoFromSource : null;
        }
      }

      const root = getScopeRoot();
      const scopedPicker = root.querySelector('product-info .ecoseal-merged-picker');
      if (scopedPicker) return scopedPicker.closest('product-info');

      const fallbackPicker = document.querySelector('product-info .ecoseal-merged-picker');
      return fallbackPicker ? fallbackPicker.closest('product-info') : null;
    },

    getMergedPicker: function(productInfo = null) {
      const scope = productInfo || this.getProductInfo();
      if (scope && scope.querySelector) {
        const merged = scope.querySelector('.ecoseal-merged-picker');
        if (merged) return merged;
      }

      const root = getScopeRoot();
      return root.querySelector('.ecoseal-merged-picker') || document.querySelector('.ecoseal-merged-picker');
    },

    getSelectElement: function(productInfo = null) {
      const scope = productInfo || this.getProductInfo();
      if (!scope || !scope.querySelector) return null;

      return (
        scope.querySelector('variant-selects[data-primary] select[id^="Option-"][name^="options["]') ||
        scope.querySelector('select[id^="Option-"][name^="options["]') ||
        null
      );
    },

    getCurrentVariantSku: function(productInfo = null) {
      const selectElement = this.getSelectElement(productInfo);
      if (!selectElement || selectElement.selectedIndex < 0) return '';

      const option = selectElement.options[selectElement.selectedIndex];
      return option ? (option.dataset.variantSku || '').trim() : '';
    },

    getActiveWorld: function(productInfo = null) {
      const merged = this.getMergedPicker(productInfo);
      if (merged && merged.dataset.activeWorld) {
        return merged.dataset.activeWorld;
      }

      const currentSku = this.getCurrentVariantSku(productInfo);
      if (this.isAnySealSku(currentSku)) return 'anyseal';
      if (currentSku) return 'sealit';
      return 'chooser';
    },

    getActiveSealItView: function(productInfo = null) {
      const merged = this.getMergedPicker(productInfo);
      return merged && merged.dataset.sealitView ? merged.dataset.sealitView : 'custom';
    },

    isFixedSetSku: function(sku) {
      const safeSku = (sku || '').trim();
      if (!safeSku) return false;

      if (Array.isArray(window.ecosealFixedSetSkus) && window.ecosealFixedSetSkus.indexOf(safeSku) !== -1) {
        return true;
      }

      const carouselConfig = window.EcoSealCarouselPicker && window.EcoSealCarouselPicker.Config;
      if (carouselConfig && Array.isArray(carouselConfig.fixedSets)) {
        return carouselConfig.fixedSets.some((set) => set && String(set.sku || '').trim() === safeSku);
      }

      return false;
    },

    isAnySealSku: function(sku) {
      const safeSku = (sku || '').trim();
      const config = this.getConfig();
      const starterSku = config.anySealStarterVariant && config.anySealStarterVariant.sku
        ? String(config.anySealStarterVariant.sku).trim()
        : '_ECOPRO1SET_ANYSEAL';
      return !!safeSku && safeSku === starterSku;
    },

    getStarterVariant: function(jarCode) {
      return this.getConfig().starterVariants[jarCode] || null;
    },

    getAddOnVariant: function(jarCode) {
      return this.getConfig().addOnVariants[jarCode] || null;
    },

    getAnySealStarterVariant: function() {
      return this.getConfig().anySealStarterVariant || null;
    },

    getAnySealAddOnVariant: function() {
      return this.getConfig().anySealAddOnVariant || null;
    },

    getValidSelections: function(selections, selectedGroups) {
      return (Array.isArray(selections) ? selections : [])
        .slice(0, selectedGroups)
        .filter((selection) => selection !== null);
    },

    getTieredDiscountByExtraUnits: function(extraUnitCount) {
      const safeCount = Math.max(0, parseInt(extraUnitCount, 10) || 0);
      let percentage = 0;

      if (safeCount >= 9) {
        percentage = 15;
      } else if (safeCount >= 6) {
        percentage = 10;
      } else if (safeCount >= 3) {
        percentage = 5;
      }

      return {
        percentage,
        extraUnitCount: safeCount
      };
    },

    getSealItDiscountTier: function(additionalCounts, quantityMultiplier = 1) {
      const counts = additionalCounts && typeof additionalCounts === 'object' ? additionalCounts : {};
      const safeMultiplier = Math.max(1, parseInt(quantityMultiplier, 10) || 1);
      let additionalGlassCount = 0;

      Object.keys(counts).forEach((jarCode) => {
        const trioCount = Math.max(0, parseInt(counts[jarCode], 10) || 0);
        if (!trioCount) return;

        additionalGlassCount += trioCount * safeMultiplier * 3;
      });

      return this.getTieredDiscountByExtraUnits(additionalGlassCount);
    },

    getAnySealDiscountTier: function(extraPackCount, quantityMultiplier = 1) {
      const safeExtraPackCount = Math.max(0, parseInt(extraPackCount, 10) || 0);
      const safeMultiplier = Math.max(1, parseInt(quantityMultiplier, 10) || 1);
      return this.getTieredDiscountByExtraUnits(safeExtraPackCount * safeMultiplier * 3);
    },

    applyPercentageDiscount: function(amount, percentage) {
      const safeAmount = parseInt(amount, 10) || 0;
      const safePercentage = Math.max(0, parseInt(percentage, 10) || 0);
      if (safeAmount <= 0 || safePercentage <= 0) return safeAmount;

      const discountAmount = Math.floor(safeAmount * safePercentage / 100);
      return safeAmount - discountAmount;
    },

    applyLineItemPercentageDiscount: function(unitPrice, quantity, percentage) {
      const safeUnitPrice = parseInt(unitPrice, 10) || 0;
      const safeQuantity = Math.max(0, parseInt(quantity, 10) || 0);
      if (safeUnitPrice <= 0 || safeQuantity <= 0) return 0;

      const discountedUnitPrice = this.applyPercentageDiscount(safeUnitPrice, percentage);
      return discountedUnitPrice * safeQuantity;
    },

    getEffectiveQuantity: function(source = null) {
      const productInfo = this.getProductInfo(source);
      if (!productInfo) return 1;

      const selectors = [
        '.quantity__input-sticky',
        '.quantity__input-primary',
        'input[name="quantity"]'
      ];

      for (let i = 0; i < selectors.length; i += 1) {
        const input = productInfo.querySelector(selectors[i]);
        const value = input ? parseInt(input.value, 10) : NaN;
        if (!Number.isNaN(value) && value > 0) return value;
      }

      return 1;
    },

    collectLineItemProperties: function(form) {
      const properties = {};
      if (!(form instanceof HTMLFormElement)) return properties;

      const fields = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
      fields.forEach((field) => {
        if (!field || field.disabled) return;

        const name = field.getAttribute('name') || '';
        const match = name.match(/^properties\[(.*)\]$/);
        if (!match || !match[1]) return;

        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
        properties[match[1]] = field.value;
      });

      return properties;
    },

    createEmptyBundle: function(mode, quantityMultiplier, properties = {}) {
      return {
        mode: mode,
        selectedGroups: 0,
        quantityMultiplier: quantityMultiplier,
        items: [],
        baseTotalPrice: 0,
        totalPrice: 0,
        totalCompareAtPrice: 0,
        discountPercentage: 0,
        discountAmount: 0,
        available: false,
        errorMessage: '',
        properties: properties,
        requiresMultipartAdd: false
      };
    },

    buildCustomBundle: function(options = {}) {
      const picker = window.EcoSealPicker;
      const state = picker && picker.state ? picker.state : null;
      const source = options.source || null;
      const selections = options.selections || (state ? state.allSelections : []);
      const selectedGroups = options.selectedGroups || (state ? state.selectedGroups : 0);
      const quantityMultiplier = options.quantityMultiplier || this.getEffectiveQuantity(source);
      const properties = this.collectLineItemProperties(options.form);
      const validSelections = this.getValidSelections(selections, selectedGroups);
      const ignoreCompareAtPrice = window.theme && window.theme.shopSettings && window.theme.shopSettings.ignoreCompareAtPrice;
      const bundle = this.createEmptyBundle('custom', quantityMultiplier, properties);

      bundle.selectedGroups = selectedGroups;
      bundle.validSelections = validSelections;

      if (!validSelections.length || validSelections.length !== selectedGroups) {
        bundle.errorMessage = 'Die gewaehlte Konfiguration ist aktuell nicht verfuegbar.';
        return bundle;
      }

      const starterJarCode = validSelections[0];
      const starterVariant = this.getStarterVariant(starterJarCode);
      if (!starterVariant || !starterVariant.variantId) {
        bundle.errorMessage = 'Das passende Starterset ist aktuell nicht verfuegbar.';
        return bundle;
      }

      let allItemsAvailable = starterVariant.available !== false;
      const starterItem = {
        id: starterVariant.variantId,
        quantity: quantityMultiplier
      };

      if (Object.keys(properties).length > 0) {
        starterItem.properties = properties;
      }

      bundle.items.push(starterItem);

      const starterBasePrice = (starterVariant.price || 0) * quantityMultiplier;
      const starterCompareAtPrice = (ignoreCompareAtPrice ? (starterVariant.price || 0) : (starterVariant.compareAtPrice || starterVariant.price || 0)) * quantityMultiplier;
      bundle.baseTotalPrice += starterBasePrice;
      bundle.totalPrice += starterBasePrice;
      bundle.totalCompareAtPrice += starterCompareAtPrice;

      const additionalCounts = {};
      validSelections.slice(1).forEach((jarCode) => {
        additionalCounts[jarCode] = (additionalCounts[jarCode] || 0) + 1;
      });

      const discountTier = this.getSealItDiscountTier(additionalCounts, quantityMultiplier);
      bundle.discountPercentage = discountTier.percentage;

      Object.keys(additionalCounts).forEach((jarCode) => {
        const addOnVariant = this.getAddOnVariant(jarCode);
        if (!addOnVariant || !addOnVariant.variantId) {
          bundle.errorMessage = 'Die gewaehlten Zusatzglaeser sind aktuell nicht verfuegbar.';
          return;
        }

        if (addOnVariant.available === false) {
          allItemsAvailable = false;
        }

        const trioCount = additionalCounts[jarCode];
        const baseQuantity = addOnVariant.quantityMode === 'bundle_of_3' ? trioCount : trioCount * 3;
        const finalQuantity = baseQuantity * quantityMultiplier;

        bundle.items.push({
          id: addOnVariant.variantId,
          quantity: finalQuantity
        });

        const addOnUnitPrice = addOnVariant.price || 0;
        const addOnBasePrice = addOnUnitPrice * finalQuantity;
        const addOnCompareAtPrice = (ignoreCompareAtPrice ? (addOnVariant.price || 0) : (addOnVariant.compareAtPrice || addOnVariant.price || 0)) * finalQuantity;
        const discountedAddOnPrice = this.applyLineItemPercentageDiscount(addOnUnitPrice, finalQuantity, bundle.discountPercentage);

        bundle.baseTotalPrice += addOnBasePrice;
        bundle.totalPrice += discountedAddOnPrice;
        bundle.totalCompareAtPrice += addOnCompareAtPrice;
        bundle.discountAmount += addOnBasePrice - discountedAddOnPrice;
      });

      if (bundle.errorMessage) return bundle;

      bundle.totalCompareAtPrice = ignoreCompareAtPrice ? bundle.totalPrice : Math.max(bundle.totalCompareAtPrice, bundle.baseTotalPrice);
      bundle.available = allItemsAvailable;
      bundle.requiresMultipartAdd = bundle.items.length > 1;

      if (!bundle.available) {
        bundle.errorMessage = 'Die gewaehlte Konfiguration ist aktuell nicht verfuegbar.';
      }

      return bundle;
    },

    buildAnySealBundle: function(options = {}) {
      const picker = window.EcoSealAnySealPicker;
      const state = picker && picker.state ? picker.state : null;
      const source = options.source || null;
      const quantityMultiplier = options.quantityMultiplier || this.getEffectiveQuantity(source);
      const selectedPacks = options.selectedPacks || (state ? state.selectedPacks : 1);
      const properties = this.collectLineItemProperties(options.form);
      const extraPackCount = Math.max(0, selectedPacks - 1);
      const ignoreCompareAtPrice = window.theme && window.theme.shopSettings && window.theme.shopSettings.ignoreCompareAtPrice;
      const bundle = this.createEmptyBundle('anyseal', quantityMultiplier, properties);

      bundle.selectedGroups = selectedPacks;
      bundle.selectedPacks = selectedPacks;
      bundle.totalUnitCount = selectedPacks * 3;
      bundle.extraPackCount = extraPackCount;

      const starterVariant = this.getAnySealStarterVariant();
      if (!starterVariant || !starterVariant.variantId) {
        bundle.errorMessage = 'Das AnySeal Starterset ist aktuell nicht verfuegbar.';
        return bundle;
      }

      let allItemsAvailable = starterVariant.available !== false;
      const starterItem = {
        id: starterVariant.variantId,
        quantity: quantityMultiplier
      };

      if (Object.keys(properties).length > 0) {
        starterItem.properties = properties;
      }

      bundle.items.push(starterItem);

      const starterBasePrice = (starterVariant.price || 0) * quantityMultiplier;
      const starterCompareAtPrice = (ignoreCompareAtPrice ? (starterVariant.price || 0) : (starterVariant.compareAtPrice || starterVariant.price || 0)) * quantityMultiplier;
      bundle.baseTotalPrice += starterBasePrice;
      bundle.totalPrice += starterBasePrice;
      bundle.totalCompareAtPrice += starterCompareAtPrice;

      const discountTier = this.getAnySealDiscountTier(extraPackCount, quantityMultiplier);
      bundle.discountPercentage = discountTier.percentage;

      if (extraPackCount > 0) {
        const addOnVariant = this.getAnySealAddOnVariant();
        if (!addOnVariant || !addOnVariant.variantId) {
          bundle.errorMessage = 'Die zusaetzlichen AnySeal Deckel sind aktuell nicht verfuegbar.';
          return bundle;
        }

        if (addOnVariant.available === false) {
          allItemsAvailable = false;
        }

        const finalQuantity = extraPackCount * quantityMultiplier;
        bundle.items.push({
          id: addOnVariant.variantId,
          quantity: finalQuantity
        });

        const addOnUnitPrice = addOnVariant.price || 0;
        const addOnBasePrice = addOnUnitPrice * finalQuantity;
        const addOnCompareAtPrice = (ignoreCompareAtPrice ? (addOnVariant.price || 0) : (addOnVariant.compareAtPrice || addOnVariant.price || 0)) * finalQuantity;
        const discountedAddOnPrice = this.applyLineItemPercentageDiscount(addOnUnitPrice, finalQuantity, bundle.discountPercentage);

        bundle.baseTotalPrice += addOnBasePrice;
        bundle.totalPrice += discountedAddOnPrice;
        bundle.totalCompareAtPrice += addOnCompareAtPrice;
        bundle.discountAmount += addOnBasePrice - discountedAddOnPrice;
      }

      bundle.totalCompareAtPrice = ignoreCompareAtPrice ? bundle.totalPrice : Math.max(bundle.totalCompareAtPrice, bundle.baseTotalPrice);
      bundle.available = allItemsAvailable;
      bundle.requiresMultipartAdd = extraPackCount > 0;

      if (!bundle.available) {
        bundle.errorMessage = 'Die gewaehlte AnySeal Konfiguration ist aktuell nicht verfuegbar.';
      }

      return bundle;
    },

    buildInactiveBundle: function(options = {}) {
      const quantityMultiplier = options.quantityMultiplier || this.getEffectiveQuantity(options.source || null);
      const bundle = this.createEmptyBundle('chooser', quantityMultiplier, {});
      bundle.errorMessage = Config.translations.get('chooseWorldFirst');
      return bundle;
    },

    getActiveBundle: function(options = {}) {
      const productInfo = this.getProductInfo(options.source || options.form || null);
      const activeWorld = this.getActiveWorld(productInfo);

      if (activeWorld === 'anyseal') {
        return this.buildAnySealBundle(options);
      }

      if (activeWorld === 'sealit') {
        return this.buildCustomBundle(options);
      }

      return this.buildInactiveBundle(options);
    },

    syncMirroredQuantities: function(productInfo = null) {
      const scope = productInfo || this.getProductInfo();
      if (!scope) return;

      const quantity = this.getEffectiveQuantity(scope);
      scope.querySelectorAll('product-form form[data-type="add-to-cart-form"]').forEach((form) => {
        const visibleQuantityInput = form.querySelector('.quantity__input-primary, .quantity__input-sticky');
        if (visibleQuantityInput) {
          visibleQuantityInput.value = quantity;
          return;
        }

        let mirroredInput = form.querySelector('input.ecoseal-mirrored-quantity[name="quantity"]');
        if (!mirroredInput) {
          mirroredInput = DOMHelper.createElement('input', {
            attributes: {
              type: 'hidden',
              name: 'quantity',
              value: quantity
            }
          });
          mirroredInput.classList.add('ecoseal-mirrored-quantity');
          form.appendChild(mirroredInput);
        } else {
          mirroredInput.value = quantity;
        }
      });
    },

    formatMoneyHtml: function(cents) {
      const safeCents = typeof cents === 'number' ? cents : parseInt(cents || 0, 10) || 0;
      let formatted = `${safeCents / 100}`;

      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
        formatted = window.theme.Currency.formatMoney(safeCents, window.theme.shopSettings && window.theme.shopSettings.moneyFormat);
      }

      if (window.theme && window.theme.shopSettings && window.theme.shopSettings.currencyCode && window.theme.shopSettings.isoCode) {
        formatted += ` ${window.theme.shopSettings.isoCode}`;
      }

      return `<price-money><bdi>${formatted}</bdi></price-money>`;
    },

    renderPriceBlock: function(container, bundle) {
      if (!container || !bundle) return;

      const existingPrice = container.querySelector('.price');
      const ignoreCompareAtPrice = window.theme && window.theme.shopSettings && window.theme.shopSettings.ignoreCompareAtPrice;
      const showCompareAtPrice = !ignoreCompareAtPrice && bundle.totalCompareAtPrice > bundle.totalPrice;
      const baseClasses = (existingPrice ? existingPrice.className : 'price')
        .split(/\s+/)
        .filter(Boolean)
        .filter((className) => className !== 'price--on-sale' && className !== 'price--sold-out');

      if (showCompareAtPrice) {
        baseClasses.push('price--on-sale');
      }
      if (!bundle.available) {
        baseClasses.push('price--sold-out');
      }

      const currentPriceHtml = this.formatMoneyHtml(bundle.totalPrice);
      const salePriceHtml = showCompareAtPrice
        ? `
            <div class="price__sale">
              <dt class="price__compare visually-hidden">Regular price</dt>
              <dd class="price__compare">
                <s class="price-item price-item--regular">${this.formatMoneyHtml(bundle.totalCompareAtPrice)}</s>
              </dd>
              <dt class="visually-hidden">Sale price</dt>
              <dd class="price__last">
                <span class="price-item price-item--sale">${currentPriceHtml}</span>
              </dd>
            </div>
          `
        : '';

      container.innerHTML = `
        <div class="${baseClasses.join(' ')}">
          <dl>
            <div class="price__regular">
              <dt class="visually-hidden">Regular price</dt>
              <dd class="price__last">
                <span class="price-item price-item--regular">${currentPriceHtml}</span>
              </dd>
            </div>
            ${salePriceHtml}
          </dl>
        </div>
      `;
    },

    updatePrices: function(productInfo, bundle) {
      if (!productInfo || !bundle) return;

      const priceBlocks = productInfo.querySelectorAll('[id^="price-"], [id^="Price-Alt-"]');
      priceBlocks.forEach((block) => this.renderPriceBlock(block, bundle));
    },

    ensureCheckoutReplacement: function(productForm) {
      if (!productForm || !productForm.querySelector) return null;

      const paymentButton = productForm.querySelector('.shopify-payment-button');
      if (!paymentButton) return null;

      let replacement = productForm.querySelector('.ecoseal-custom-checkout');
      if (!replacement) {
        replacement = DOMHelper.createElement('div', {
          className: 'shopify-payment-button ecoseal-custom-checkout',
          attributes: {
            hidden: 'hidden'
          }
        });

        const button = DOMHelper.createElement('button', {
          className: this.checkoutButtonClassName,
          text: Config.translations.get('checkout'),
          attributes: {
            type: 'button',
            'data-custom-checkout-trigger': 'true'
          },
          styles: {
            width: '100%'
          }
        });

        replacement.appendChild(button);
        paymentButton.insertAdjacentElement('afterend', replacement);
      }

      return replacement;
    },

    updateCheckoutUi: function(productInfo, bundle, state) {
      if (!productInfo || !productInfo.querySelectorAll) return;

      const activeWorld = state && state.activeWorld ? state.activeWorld : 'chooser';
      const isFixedSet = !!(state && state.isFixedSet);
      const hideAllCheckout = false;
      const needsCustomCheckout = !hideAllCheckout && !isFixedSet && bundle && bundle.requiresMultipartAdd;

      productInfo.querySelectorAll('product-form').forEach((productForm) => {
        const paymentButton = productForm.querySelector('.shopify-payment-button');
        if (!paymentButton) return;

        const replacement = this.ensureCheckoutReplacement(productForm);
        const replacementButton = replacement ? replacement.querySelector('[data-custom-checkout-trigger]') : null;

        paymentButton.hidden = hideAllCheckout || needsCustomCheckout;
        paymentButton.style.display = hideAllCheckout || needsCustomCheckout ? 'none' : '';

        if (replacement) {
          replacement.hidden = !needsCustomCheckout;
          if (replacementButton) {
            replacementButton.disabled = !needsCustomCheckout || !(bundle && bundle.available);
          }
        }
      });
    },

    updateSubmitButtons: function(productInfo, bundle, state) {
      if (!productInfo) return;

      const activeWorld = state && state.activeWorld ? state.activeWorld : 'chooser';
      const isFixedSet = !!(state && state.isFixedSet);
      const selectElement = this.getSelectElement(productInfo);
      const selectedOption = selectElement && selectElement.selectedIndex >= 0
        ? selectElement.options[selectElement.selectedIndex]
        : null;
      const variantAvailable = selectedOption ? !selectedOption.disabled : true;

      productInfo.querySelectorAll('product-form').forEach((productForm) => {
        const submitButton = productForm.submitButton || productForm.querySelector('[name="add"][type="submit"]');
        if (!submitButton) return;

        if (activeWorld === 'chooser') {
          if (variantAvailable) {
            submitButton.removeAttribute('disabled');
          } else {
            submitButton.setAttribute('disabled', 'disabled');
          }
          this.showError(productForm, false);
          return;
        }

        if (isFixedSet) {
          if (variantAvailable) {
            submitButton.removeAttribute('disabled');
          } else {
            submitButton.setAttribute('disabled', 'disabled');
          }
          this.showError(productForm, false);
          return;
        }

        if (bundle && bundle.available) {
          submitButton.removeAttribute('disabled');
          this.showError(productForm, false);
        } else {
          submitButton.setAttribute('disabled', 'disabled');
        }
      });
    },

    sync: function(source = null) {
      const productInfo = this.getProductInfo(source);
      if (!productInfo) return;

      this.syncMirroredQuantities(productInfo);

      const activeWorld = this.getActiveWorld(productInfo);
      const currentSku = this.getCurrentVariantSku(productInfo);
      const isFixedSet = activeWorld === 'sealit' && this.isFixedSetSku(currentSku);
      const bundle = activeWorld === 'chooser'
        ? this.buildInactiveBundle({ source: productInfo })
        : (activeWorld === 'anyseal' ? this.buildAnySealBundle({ source: productInfo }) : this.buildCustomBundle({ source: productInfo }));

      if (activeWorld !== 'chooser' && !isFixedSet) {
        this.updatePrices(productInfo, bundle);
      }

      this.updateCheckoutUi(productInfo, bundle, { activeWorld, isFixedSet });
      this.updateSubmitButtons(productInfo, bundle, { activeWorld, isFixedSet });
    },

    isManagedForm: function(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      if (form.dataset.type !== 'add-to-cart-form') return false;
      const productInfo = this.getProductInfo(form);
      return !!productInfo;
    },

    showError: function(productForm, message) {
      if (!productForm || typeof productForm.handleErrorMessage !== 'function') return;
      productForm.handleErrorMessage(message || false);
    },

    setLoadingState: function(productForm, sourceButton, isLoading) {
      if (!productForm) return;

      const submitButton = productForm.submitButton || productForm.querySelector('[name="add"][type="submit"]');
      const checkoutButton = productForm.querySelector('[data-custom-checkout-trigger]');
      [submitButton, checkoutButton].forEach((button) => {
        if (!button) return;
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        } else {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }
      });

      if (sourceButton && sourceButton !== submitButton && isLoading) {
        sourceButton.setAttribute('aria-disabled', 'true');
        sourceButton.classList.add('loading');
      }
    },

    getCheckoutUrl: function() {
      const rootUrl = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
      return rootUrl.endsWith('/') ? `${rootUrl}checkout` : `${rootUrl}/checkout`;
    },

    submitBundle: function(productForm, bundle, options = {}) {
      const sourceButton = options.sourceButton || null;
      const redirectToCheckout = options.redirectToCheckout === true;
      const cart = productForm ? (productForm.cart || document.querySelector('mini-cart')) : document.querySelector('mini-cart');
      const sections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : [];

      const body = {
        items: bundle.items,
        sections_url: window.location.pathname
      };
      if (sections.length) body.sections = sections;

      const config = fetchConfig('javascript');
      config.body = JSON.stringify(body);

      this.showError(productForm, false);
      this.setLoadingState(productForm, sourceButton, true);

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(productForm, response.description || response.message || theme.cartStrings.error);
            return;
          }

          if (redirectToCheckout) {
            window.location.href = this.getCheckoutUrl();
            return;
          }

          if (!cart) {
            window.location = theme.routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ecoseal-custom-bundle',
            productVariantId: bundle.items[0] && bundle.items[0].id,
            cartData: response,
          });

          cart.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
          this.showError(productForm, theme.cartStrings.error);
        })
        .finally(() => {
          this.setLoadingState(productForm, sourceButton, false);
        });
    },

    shouldUseNativeFlow: function(activeWorld, isFixedSet, bundle) {
      if (activeWorld === 'chooser') return true;
      if (isFixedSet) return true;
      if (!bundle) return false;
      return !bundle.requiresMultipartAdd;
    },

    handleSubmit: function(event) {
      const form = event.target;
      if (!this.isManagedForm(form)) return;

      const productInfo = this.getProductInfo(form);
      const activeWorld = this.getActiveWorld(productInfo);
      const currentSku = this.getCurrentVariantSku(productInfo);
      const isFixedSet = activeWorld === 'sealit' && this.isFixedSetSku(currentSku);
      const productForm = form.closest('product-form');
      if (!productForm) return;
      if (productForm.submitButton && productForm.submitButton.getAttribute('aria-disabled') === 'true') return;

      if (activeWorld === 'chooser') {
        this.showError(productForm, false);
        return;
      }

      if (isFixedSet) return;

      if (typeof productForm.blockSubmitIfRequiredFieldsEmpty === 'function' && !productForm.blockSubmitIfRequiredFieldsEmpty(event)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      const bundle = this.getActiveBundle({
        source: productInfo,
        form: form
      });

      if (this.shouldUseNativeFlow(activeWorld, isFixedSet, bundle)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!bundle.available) {
        this.showError(productForm, bundle.errorMessage || theme.cartStrings.error);
        return;
      }

      this.submitBundle(productForm, bundle, {
        sourceButton: productForm.submitButton,
        redirectToCheckout: false
      });
    },

    handleCheckoutClick: function(event) {
      const button = event.target && event.target.closest ? event.target.closest('[data-custom-checkout-trigger]') : null;
      if (!button) return;

      const productForm = button.closest('product-form');
      const form = productForm ? productForm.querySelector('form[data-type="add-to-cart-form"]') : null;
      if (!productForm || !form || !this.isManagedForm(form)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const productInfo = this.getProductInfo(button);
      const bundle = this.getActiveBundle({
        source: productInfo,
        form: form
      });

      if (!bundle.available) {
        this.showError(productForm, bundle.errorMessage || theme.cartStrings.error);
        return;
      }

      this.submitBundle(productForm, bundle, {
        sourceButton: button,
        redirectToCheckout: true
      });
    },

    handleQuantityInput: function(event) {
      const target = event.target;
      if (!target || !(target instanceof HTMLInputElement)) return;
      if (target.name !== 'quantity') return;

      const productInfo = this.getProductInfo(target);
      if (!productInfo) return;

      this.sync(productInfo);
    },

    bindGlobalEvents: function() {
      if (window.__ecosealBundleManagerBound) return;
      window.__ecosealBundleManagerBound = true;

      document.addEventListener('submit', this.handleSubmit.bind(this), true);
      document.addEventListener('click', this.handleCheckoutClick.bind(this), true);
      document.addEventListener('input', this.handleQuantityInput.bind(this), true);
      document.addEventListener('change', this.handleQuantityInput.bind(this), true);
      document.addEventListener('product-info:loaded', () => this.sync());
      document.addEventListener('quickview:loaded', () => this.sync());

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        subscribe(window.PUB_SUB_EVENTS.variantChange, () => this.sync());
      }
    }
  };

  // The main application
  const EcoSealPicker = {
    // Application state
    state: {
      allSelections: [
        Config.defaults.initialJarType, 
        Config.defaults.initialJarType, 
        Config.defaults.initialJarType, 
        Config.defaults.initialJarType
      ],  // Default to small twist-off jars
      previousSelections: [null, null, null, null], // Track previous selections for animations
      currentSetIndex: 0,                      // Current trio being configured
      selectedGroups: Config.defaults.groupCount, // Number of trios (1-4)
      discountLevels: [
        { quantity: 1, discount: 0 },
        { quantity: 2, discount: 5 },
        { quantity: 3, discount: 10 },
        { quantity: 4, discount: 15 }
      ]
    },

    getContainer: function() {
      const scope = getScopeRoot();
      if (scope) {
        return scope.querySelector('.ecoseal-variant-picker--sealit') || scope.querySelector('#custom-picker.ecoseal-variant-picker');
      }

      return document.querySelector('.ecoseal-variant-picker--sealit') || document.querySelector('#custom-picker.ecoseal-variant-picker');
    },
    
    /**
     * Initialize the picker
     */
    init: function() {
      // Guard per rendered picker instance (quick-view content can be injected multiple times)
      const container = this.getContainer();
      if (!container) return;
      if (container.dataset.ecosealPickerInitialized === 'true') return;
      container.dataset.ecosealPickerInitialized = 'true';
      
      // Create discount progress bar
      this.createDiscountProgressBar();
      
      // Try to initialize from URL variant param first
      const initializedFromUrl = this.initFromUrl();
      
      // Set up event listeners
      this.setupEventListeners();
      BundleManager.bindGlobalEvents();
      
      if (initializedFromUrl) {
        // For URL variants, Shopify has already selected the variant,
        // so we just need to update our UI to match
        this.updateUI(false);
      } else {
        // Check if Shopify's default variant matches our default settings
        // If it doesn't, we'll need to update the variant
        const shouldUpdateVariant = this.doesDefaultDifferFromShopify();
        
        // Initialize with default state
        this.resetState(shouldUpdateVariant);
      }
      
      // Update discount progress bar
      this.updateDiscountProgressBar();
      this.syncExternalState();
    },
    
    /**
     * Initialize picker state from a variant ID in the URL
     * @return {boolean} - Whether initialization from URL was successful
     */
    initFromUrl: function() {
      // Get variant ID from URL if present
      const variantId = this.getUrlParameter('variant');
      if (!variantId) return false;
      
      // Find the variant by ID
      const variant = VariantManager.findById(variantId);
      if (!variant || !variant.sku) return false;
      
      // Decode the SKU to get the jar configuration
      const decoded = VariantManager.decodeSku(variant.sku);
      if (!decoded) return false;
      
      // Update state with decoded selections
      this.state.selectedGroups = decoded.quantity;
      
      // Fill in selections
      for (let i = 0; i < 4; i++) {
        if (i < decoded.selections.length) {
          this.state.allSelections[i] = decoded.selections[i];
        } else {
          // Default value for unused slots
          this.state.allSelections[i] = Config.defaults.initialJarType;
        }
      }
      
      // Update the UI to match the variant, but don't trigger another variant selection
      // since Shopify already selected the variant from the URL
      this.updateSelectionOverview();
      
      // Update discount progress bar
      this.updateDiscountProgressBar();
      
      return true;
    },
    
    /**
     * Helper to get URL parameter value
     * @param {string} name - Parameter name to get
     * @return {string} - Parameter value or empty string
     */
    getUrlParameter: function(name) {
      name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
      const results = regex.exec(location.search);
      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    },
    
    /**
     * Reset application state
     * @param {boolean} updateVariant - Whether to update the variant selection
     */
    resetState: function(updateVariant = false) {
      this.state.allSelections = [
        Config.defaults.initialJarType,
        Config.defaults.initialJarType,
        Config.defaults.initialJarType,
        Config.defaults.initialJarType
      ];
      this.state.currentSetIndex = 0;
      this.state.selectedGroups = Config.defaults.groupCount;
      
      // Update UI
      this.updateSelectionOverview();
      
      // Update discount progress bar
      this.updateDiscountProgressBar();
      
      // Only update variant if required
      if (updateVariant) {
        VariantManager.updateVariantBySelection(this.state.allSelections, this.state.selectedGroups);
      }

      this.syncExternalState();
    },
    
    /**
     * Check if our default configuration differs from the Shopify-selected variant
     * @return {boolean} - True if our default configuration differs from Shopify's default
     */
    doesDefaultDifferFromShopify: function() {
      // Get currently selected variant option
      const selectElement = VariantManager.getSelectElement();
      if (!selectElement) return false;
      
      const currentSelectedOption = selectElement.options[selectElement.selectedIndex];
      if (!currentSelectedOption) return false;
      
      const currentSku = (currentSelectedOption.dataset.variantSku || '').trim();
      if (!currentSku) return false;
      
      // Decode the current variant's SKU
      const decoded = VariantManager.decodeSku(currentSku);
      if (!decoded) return false;
      
      // Check if quantity differs
      if (decoded.quantity !== Config.defaults.groupCount) return true;
      
      // Check if jar selections differ from our defaults
      if (decoded.selections.length !== decoded.quantity) return true;
      
      for (let i = 0; i < decoded.quantity; i++) {
        if (decoded.selections[i] !== Config.defaults.initialJarType) return true;
      }
      
      // If we got here, Shopify's default variant matches our default settings
      return false;
    },
    
    /**
     * Update quantity selection UI
     * @param {number} quantity - New quantity to select
     */
    updateQuantitySelection: function(quantity) {
      this.state.selectedGroups = quantity;
      
      // Reset to first trio if current trio is beyond new quantity
      if (this.state.currentSetIndex >= quantity) {
        this.state.currentSetIndex = 0;
      }
      
      // Update UI
      this.updateSelectionOverview();
      
      // Update discount progress bar
      this.updateDiscountProgressBar();
      
      // Update variant selection to reflect changes
      VariantManager.updateVariantBySelection(this.state.allSelections, this.state.selectedGroups);
      this.syncExternalState();
    },
    
    /**
     * Update the selection overview UI
     */
    updateSelectionOverview: function() {
      const overview = DOMHelper.getElement("#selection-overview");
      const modeTitle = DOMHelper.getElement("#mode-title");
      
      if (!overview) {
        return;
      }
      
      // Update title for overview mode
      if (modeTitle) {
        modeTitle.textContent = Config.translations.get('yourSelection');
      }
      
      // First check if we need to add the EcoSeal Pro item
      let sealerItem = overview.querySelector('.trio-item.non-selectable');
      if (!sealerItem) {
        sealerItem = this.createNonSelectableItem();
        overview.appendChild(sealerItem);
      }
      
      // Handle each trio position
      for (let i = 0; i < 4; i++) {
        const existingItem = overview.querySelector(`.trio-item[data-trio-index="${i}"]:not(.non-selectable)`);
        const selection = this.state.allSelections[i];
        const previousSelection = this.state.previousSelections[i];
        
        // If this position is not in the selected groups, remove the item if it exists with animation
        if (i >= this.state.selectedGroups) {
          if (existingItem) {
            existingItem.classList.add('row-exit');
            setTimeout(() => {
              if (existingItem.parentNode) {
                existingItem.parentNode.removeChild(existingItem);
              }
            }, 300);
          }
          continue;
        }
        
        // Skip null selections
        if (selection === null) continue;
        
        // If there's no existing item, create it with entrance animation
        if (!existingItem) {
          const newTrioItem = this.createTrioItem(selection, i);
          newTrioItem.classList.add('row-enter');
          overview.appendChild(newTrioItem);
          
          // Remove the animation class after it completes
          setTimeout(() => {
            newTrioItem.classList.remove('row-enter');
          }, 300);
          continue;
        }
        
        // Check if selection changed (jar type switching)
        const selectionChanged = previousSelection !== null && previousSelection !== selection;
        
        // If the selection changed, update the content of the existing item with animation
        if (selectionChanged) {
          // Get references to the existing elements
          const existingPreview = existingItem.querySelector('.selection-preview');
          const existingDescription = existingItem.querySelector('.trio-description');
          
          if (existingPreview && existingDescription) {
            // Create new elements with new content
            const newPreview = this.createJarPreview(selection);
            const newDescription = DOMHelper.createElement('div', {
              className: 'trio-description',
              text: `3× ${JarData.jarTypes[selection].volume} ${JarData.jarTypes[selection].type}`
            });
            
            // Add exit animation to existing content
            existingPreview.classList.add('content-exit');
            existingDescription.classList.add('content-exit');
            
            // After short delay, replace content
            setTimeout(() => {
              // Replace contents
              existingPreview.innerHTML = newPreview.innerHTML;
              existingDescription.textContent = newDescription.textContent;
              
              // Remove exit class and add enter class
              existingPreview.classList.remove('content-exit');
              existingDescription.classList.remove('content-exit');
              existingPreview.classList.add('content-enter');
              existingDescription.classList.add('content-enter');
              
              // Remove animation classes after animation completes
              setTimeout(() => {
                existingPreview.classList.remove('content-enter');
                existingDescription.classList.remove('content-enter');
              }, 300);
            }, 50);
          }
        }
      }
      
      // Update previous selections for next time
      this.state.previousSelections = [...this.state.allSelections];
    },
    
    /**
     * Create a jar preview element
     * @param {string} selection - Jar selection code
     * @return {Element} - The created jar preview element
     */
    createJarPreview: function(selection) {
      // Create circle preview with image
      const preview = DOMHelper.createElement('div', {
        className: 'selection-preview media-wrapper media-wrapper--small'
      });
      
      // Add image to preview
      const imgUrl = JarData.getJarImageUrlSmall(selection);
      if (imgUrl) {
        const img = DOMHelper.createElement('img', {
          is: 'lazy-image',
          attributes: {
            src: imgUrl,
            alt: `${JarData.jarTypes[selection].type} ${JarData.jarTypes[selection].size}`,
            loading: 'lazy',
            decoding: 'async'
          },
          styles: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
          }
        });
        
        // Theme-native loading animation (works with CSS `.media-wrapper[loading]`)
        preview.setAttribute('loading', 'true');
        const markLoaded = () => preview.removeAttribute('loading');
        if (img.complete) {
          markLoaded();
        } else {
          img.addEventListener('load', () => (window.requestIdleCallback ? window.requestIdleCallback(markLoaded, { timeout: 150 }) : setTimeout(markLoaded)), { once: true });
          img.addEventListener('error', markLoaded, { once: true });
        }

        preview.appendChild(img);
      }
      
      return preview;
    },
    
    /**
     * Create a trio item for the selection overview
     * @param {string} selection - Jar selection code
     * @param {number} index - Trio index
     * @return {Element} - The created DOM element
     */
    createTrioItem: function(selection, index) {
      // Create trio item
      const trioItem = DOMHelper.createElement('div', {
        className: 'trio-item',
        attributes: {
          'data-trio-index': index,
          'data-configure-text': Config.translations.get('clickToConfigure')
        }
      });
      
      // Create and add jar preview
      const preview = this.createJarPreview(selection);
      trioItem.appendChild(preview);
      
      // Add description text for the trio
      const description = DOMHelper.createElement('div', {
        className: 'trio-description',
        text: `3× ${JarData.jarTypes[selection].volume} ${JarData.jarTypes[selection].type}`
      });
      
      trioItem.appendChild(description);
      
      // Add a more prominent configure button with edit icon
      const configureButton = DOMHelper.createElement('button', {
        className: 'configure-button always-visible',
        html: `
          <svg class="edit-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
          </svg>
          <span>${Config.translations.get('clickToConfigure')}</span>
        `,
        attributes: {
          'type': 'button',
          'aria-label': Config.translations.get('clickToConfigure')
        }
      });
      
      trioItem.appendChild(configureButton);
      
      return trioItem;
    },
    
    /**
     * Create the non-selectable EcoSeal Pro item
     * @return {Element} - The created DOM element
     */
    createNonSelectableItem: function() {
      const sealerItem = DOMHelper.createElement('div', {
        className: 'trio-item non-selectable'
      });
      
      // Create circle preview with sealer image
      const sealerPreview = DOMHelper.createElement('div', {
        className: 'selection-preview media-wrapper media-wrapper--small'
      });
      
      const sealerImg = DOMHelper.createElement('img', {
        is: 'lazy-image',
        attributes: {
          src: JarData.getSealerImageUrlSmall(),
          alt: "EcoSeal Pro",
          loading: 'lazy',
          decoding: 'async'
        },
        styles: {
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%'
        }
      });
      
      sealerPreview.setAttribute('loading', 'true');
      const markLoaded = () => sealerPreview.removeAttribute('loading');
      if (sealerImg.complete) {
        markLoaded();
      } else {
        sealerImg.addEventListener('load', () => (window.requestIdleCallback ? window.requestIdleCallback(markLoaded, { timeout: 150 }) : setTimeout(markLoaded)), { once: true });
        sealerImg.addEventListener('error', markLoaded, { once: true });
      }

      sealerPreview.appendChild(sealerImg);
      sealerItem.appendChild(sealerPreview);
      
      // Add description text for the sealer
      const sealerDescription = DOMHelper.createElement('div', {
        className: 'trio-description',
        text: "1× " + Config.translations.get('ecosealpro')
      });
      
      sealerItem.appendChild(sealerDescription);
      
      return sealerItem;
    },
    
    /**
     * Open the configuration modal
     * @param {number} trioIndex - The index of the trio to configure
     */
    openConfigModal: function(trioIndex) {
      // Set the current trio index
      this.state.currentSetIndex = trioIndex;
      
      // Update jar grid in modal
      this.updateModalJarGrid();
      
      // Check if on mobile
      const isMobile = window.innerWidth <= 480;
      
      // Get modal elements
      const modal = DOMHelper.getElement('#jar-config-modal');
      const modalContent = DOMHelper.getElement('.modal-content');
      
      if (!modal || !modalContent) return;

      const mergedPicker = modal.closest('.ecoseal-merged-picker');
      if (mergedPicker) {
        mergedPicker.classList.add('is-modal-open');
      }
      
      // First make the modal active to show the background
      modal.classList.add('active');
      document.body.style.overflow = 'hidden'; // Prevent scrolling of background
      
      // Apply transforms without transition first 
      modalContent.style.transition = 'none';
      if (isMobile) {
        modalContent.style.transform = 'translateY(100%)';
      } else {
        modalContent.style.transform = 'translateY(20px)';
      }
      modalContent.style.opacity = '0';
      
      // Position the selected jar type in view before starting animation
      const selectedJarType = this.state.allSelections[trioIndex];
      if (selectedJarType) {
        const selectedOption = DOMHelper.getElement(`.jar-option.selected`);
        if (selectedOption) {
          // Scroll the modal container to position the selected option in view
          const modalJarContainer = DOMHelper.getElement('#modal-jar-container');
          if (modalJarContainer) {
            // Get the appropriate position to scroll to
            const optionTop = selectedOption.offsetTop;
            const containerHeight = modalJarContainer.clientHeight;
            const optionHeight = selectedOption.clientHeight;
            
            // Position the selected option in the center of the container
            const scrollPosition = optionTop - (containerHeight / 2) + (optionHeight / 2);
            modalJarContainer.scrollTop = Math.max(0, scrollPosition);
          }
        }
      }
      
      // Force a reflow to ensure the no-transition state is applied
      void modalContent.offsetWidth;
      
      // Now restore the transition and animate in
      modalContent.style.transition = 'transform 0.45s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.45s ease';
      modalContent.style.transform = 'translateY(0)';
      modalContent.style.opacity = '1';
      
      // Initialize swipe to close on mobile
      if (isMobile) {
        this.initSwipeToClose(modalContent);
      }
    },
    
    /**
     * Initialize swipe to close functionality
     * @param {Element} element - The modal content element
     */
    initSwipeToClose: function(element) {
      if (!element) return;

      // Idempotency: openConfigModal() can call this repeatedly; only bind once per modal content element.
      if (element._swipeHandlers) return;
      
      // Variables to track touch movement
      let startY = 0;
      let currentY = 0;
      let isDragging = false;
      
      // Add swipe handle area if it doesn't exist
      if (!element.querySelector('.swipe-handle-area')) {
        const swipeHandle = DOMHelper.createElement('div', {
          className: 'swipe-handle-area'
        });
        element.prepend(swipeHandle);
      }
      
      const modalHeader = DOMHelper.getElement('.modal-header');
      const modalContainer = DOMHelper.getElement('#jar-config-modal');
      
      // Touch event handlers
      const handleTouchStart = (e) => {
        // Only allow swipe from the header area or swipe handle
        const touchY = e.touches[0].clientY;
        const rect = element.getBoundingClientRect();
        const isTopArea = touchY - rect.top < 60; // Top 60px is swipeable
        
        if (isTopArea) {
          startY = e.touches[0].clientY;
          currentY = startY;
          isDragging = true;
          element.style.transition = 'none'; // Disable transition during drag
          
          // Change cursor to indicate grabbing
          if (modalHeader) {
            modalHeader.style.cursor = 'grabbing';
          }
        }
      };
      
      const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;
        
        // Only allow downward swipes
        if (deltaY > 0) {
          element.style.transform = `translateY(${deltaY}px)`;
          
          // Gradually fade the modal background as user swipes down
          if (modalContainer) {
            const opacity = Math.max(1 - (deltaY / 300), 0.5);
            modalContainer.style.backgroundColor = `rgba(0, 0, 0, ${opacity * 0.5})`;
          }
          
          e.preventDefault(); // Prevent scrolling while swiping
        }
      };
      
      const handleTouchEnd = () => {
        if (!isDragging) return;
        
        isDragging = false;
        element.style.transition = 'transform 0.45s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.45s ease';
        
        if (modalHeader) {
          modalHeader.style.cursor = '';
        }
        
        const deltaY = currentY - startY;
        
        // If swiped down more than 100px or with enough velocity, close the modal
        if (deltaY > 100) {
          element.style.transform = `translateY(100%)`;
          element.style.opacity = '0';
          
          // Reset modal background
          if (modalContainer) {
            modalContainer.style.backgroundColor = '';
          }
          
          // Close the modal after animation
          setTimeout(() => {
            this.closeConfigModal();
          }, 300);
        } else {
          // Reset position if not swiped enough
          element.style.transform = 'translateY(0)';
          element.style.opacity = '1';
          
          // Reset modal background
          if (modalContainer) {
            modalContainer.style.backgroundColor = '';
          }
        }
      };
      
      // Add event listeners
      element.addEventListener('touchstart', handleTouchStart, { passive: false });
      element.addEventListener('touchmove', handleTouchMove, { passive: false });
      element.addEventListener('touchend', handleTouchEnd);
      
      // Store reference to the event handlers for potential cleanup
      element._swipeHandlers = {
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd
      };
    },
    
    /**
     * Close the configuration modal
     */
    closeConfigModal: function() {
      const modal = DOMHelper.getElement('#jar-config-modal');
      const modalContent = DOMHelper.getElement('.modal-content');
      
      if (modal && modalContent) {
        const mergedPicker = modal.closest('.ecoseal-merged-picker');
        if (mergedPicker) {
          mergedPicker.classList.remove('is-modal-open');
        }

        // Check if on mobile
        const isMobile = window.innerWidth <= 480;
        
        // Remove the active class from modal immediately to hide the background overlay
        modal.classList.remove('active');
        
        // Make sure transition is set to the proper duration
        modalContent.style.transition = 'transform 0.45s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.45s ease';
        
        // But keep the content visible while animating out
        if (isMobile) {
          modalContent.style.transform = 'translateY(100%)';
        } else {
          modalContent.style.transform = 'translateY(20px)';
        }
        modalContent.style.opacity = '0';
        
        // Wait for animation to finish before restoring scrolling and updating UI
        setTimeout(() => {
          document.body.style.overflow = ''; // Restore scrolling
          
          // Update overview to reflect changes
          this.updateSelectionOverview();
        }, 450); // Match the animation duration
      }
    },
    
    /**
     * Update the jar grid in the modal
     */
    updateModalJarGrid: function() {
      const twistOffGrid = DOMHelper.getElement("#twist-off-grid");
      const masonGrid = DOMHelper.getElement("#mason-grid");
      const masonSection = DOMHelper.getElement('#mason-section');
      
      if (!twistOffGrid && !masonGrid) return;
      
      // Clear existing grids
      if (twistOffGrid) twistOffGrid.innerHTML = "";
      if (masonGrid) masonGrid.innerHTML = "";
      
      // Create twist-off jar options
      if (twistOffGrid) {
        JarData.groups.twistOff.forEach(jarType => {
          const jarOption = this.createModalJarOption(jarType);
          twistOffGrid.appendChild(jarOption);
        });
      }
      
      // Create mason jar options only if section exists and group has items
      const hasMasonOptions = Array.isArray(JarData.groups.mason) && JarData.groups.mason.length > 0;
      if (masonGrid && hasMasonOptions) {
        JarData.groups.mason.forEach(jarType => {
          const jarOption = this.createModalJarOption(jarType);
          masonGrid.appendChild(jarOption);
        });
      }
      // Hide mason section entirely when no options are available
      if (masonSection) {
        masonSection.style.display = hasMasonOptions ? '' : 'none';
      }
      
      // Update modal title to show which trio is being edited
      const modalTitle = DOMHelper.getElement('.modal-title');
      if (modalTitle) {
        modalTitle.textContent = `${Config.translations.get('configureTrio')} ${this.state.currentSetIndex + 1}`;
      }
    },
    
    /**
     * Create a jar option element for the modal
     * @param {string} jarType - Jar type code
     * @return {Element} - The created DOM element
     */
    createModalJarOption: function(jarType) {
      const isSelected = this.state.allSelections[this.state.currentSetIndex] === jarType;

      // External stock gating (server-provided via Liquid). We only disable when available === false.
      // If availability is unknown/null, we fail open (option remains selectable).
      const availability = window.ecosealJarAvailability && window.ecosealJarAvailability[jarType];
      const isSoldOut = availability && availability.available === false;
      
      const jarOption = DOMHelper.createElement('div', {
        className: `jar-option${isSelected ? " selected" : ""}${isSoldOut ? " is-soldout" : ""}`,
        attributes: {
          'data-jar-type': jarType,
          ...(isSoldOut ? { 'aria-disabled': 'true' } : {})
        }
      });
      
      // Create image container
      const jarImage = DOMHelper.createElement('div', {
        className: 'jar-image media-wrapper media-wrapper--small',
        styles: {
          backgroundColor: 'transparent'
        }
      });
      
      // Add image
      const imgUrl = JarData.getJarImageUrl(jarType);
      if (imgUrl) {
        const img = DOMHelper.createElement('img', {
          is: 'lazy-image',
          attributes: {
            src: imgUrl,
            alt: `${JarData.jarTypes[jarType].type} ${JarData.jarTypes[jarType].size}`,
            loading: 'lazy',
            decoding: 'async'
          },
          styles: {
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            backgroundColor: 'transparent'
          }
        });
        
        jarImage.setAttribute('loading', 'true');
        const markLoaded = () => jarImage.removeAttribute('loading');
        if (img.complete) {
          markLoaded();
        } else {
          img.addEventListener('load', () => (window.requestIdleCallback ? window.requestIdleCallback(markLoaded, { timeout: 150 }) : setTimeout(markLoaded)), { once: true });
          img.addEventListener('error', markLoaded, { once: true });
        }

        jarImage.appendChild(img);
      }

      // Sold out badge overlay
      if (isSoldOut) {
        const soldOutText = (window.ecosealTranslations && window.ecosealTranslations.soldOut) || 'Ausverkauft';
        const soldOutBadge = DOMHelper.createElement('div', {
          className: 'soldout-badge',
          text: soldOutText
        });
        jarImage.appendChild(soldOutBadge);
      }

      // Create volume label
      const volumeLabel = DOMHelper.createElement('div', {
        className: 'volume-label',
        text: `${JarData.jarTypes[jarType].volume} × 3`
      });
      
      // Add image and label to jar option
      jarOption.appendChild(jarImage);
      jarOption.appendChild(volumeLabel);
      
      // Add click handler only if selectable
      if (!isSoldOut) {
        jarOption.addEventListener('click', this.handleJarOptionClick.bind(this, jarType, jarOption));
      }
      
      return jarOption;
    },
    
    /**
     * Handle jar option click in modal
     * @param {string} jarType - Jar type code
     * @param {Element} optionElement - The clicked jar option element
     * @param {Event} e - Click event
     */
    handleJarOptionClick: function(jarType, optionElement, e) {
      // Prevent default browser action
      e.preventDefault();
      e.stopPropagation();
      
      // Save the previous selection for animation purposes
      this.state.previousSelections[this.state.currentSetIndex] = this.state.allSelections[this.state.currentSetIndex];
      
      // Update the selection state immediately
      this.state.allSelections[this.state.currentSetIndex] = jarType;
      
      // Update the variant selection
      VariantManager.updateVariantBySelection(this.state.allSelections, this.state.selectedGroups);
      this.syncExternalState();
      
      // Update UI to show selection - this is visual only
      DOMHelper.getElements('.jar-option').forEach(option => {
        option.classList.remove('selected');
      });
      optionElement.classList.add('selected');
      
      // Close the modal immediately
      this.closeConfigModal();
      
      return false; // Prevent any default behavior
    },
    
    /**
     * Handle trio item click in overview
     * @param {number} trioIndex - The index of the clicked trio
     */
    handleTrioItemClick: function(trioIndex) {
      if (trioIndex < 0 || trioIndex >= this.state.selectedGroups) return;
      
      // Find the clicked trio item
      const trioItem = DOMHelper.getElement(`.trio-item[data-trio-index="${trioIndex}"]`);
      if (!trioItem) return;
      
      // Open the modal immediately
      this.openConfigModal(trioIndex);
      
      // Add a subtle animation to the clicked item (but don't wait for it)
      trioItem.style.transition = "transform 0.3s ease";
      trioItem.style.transform = "scale(0.95)";
      
      setTimeout(() => {
        trioItem.style.transform = "scale(1)";
      }, 150);
    },
    
    /**
     * Set up all event listeners
     */
    setupEventListeners: function() {
      // Trio items in overview mode (open for editing)
      // Bind only once globally (scripts can be re-injected via quick-view) and always route to the
      // currently active picker instance in the current DOM scope.
      if (!window.__ecosealPickerTrioClickBound) {
        window.__ecosealPickerTrioClickBound = true;
        document.addEventListener('click', (e) => {
          const picker = window.EcoSealPicker;
          if (!picker || typeof picker.handleTrioItemClick !== 'function') return;
          if (!e || !e.target || typeof e.target.closest !== 'function') return;

          const trioItem = e.target.closest('.trio-item');
          if (!trioItem || trioItem.classList.contains('non-selectable')) return;

          // Ensure the click is inside the currently active picker (page vs quick-view)
          const scope = getScopeRoot();
          const container = scope && scope.querySelector ? scope.querySelector('.ecoseal-variant-picker') : null;
          if (!container || !container.contains(trioItem)) return;

          const trioIndex = parseInt(trioItem.dataset.trioIndex);
          if (Number.isNaN(trioIndex)) return;
          picker.handleTrioItemClick(trioIndex);
        });
      }
      
      // Modal close button
      const modalCloseBtn = DOMHelper.getElement('.modal-close');
      if (modalCloseBtn) {
        modalCloseBtn.addEventListener('click', () => {
          // Add a click animation to the close button
          modalCloseBtn.style.transform = "rotate(90deg)";
          setTimeout(() => this.closeConfigModal(), 200);
        });
      }
      
      // Close modal on background click
      const modalElement = DOMHelper.getElement('#jar-config-modal');
      if (modalElement) {
        modalElement.addEventListener('click', e => {
          if (e.target === modalElement) {
            this.closeConfigModal();
          }
        });
      }
      
      // Stop propagation of clicks within modal content
      const modalContent = DOMHelper.getElement('.modal-content');
      if (modalContent) {
        modalContent.addEventListener('click', e => {
          e.stopPropagation();
        });
      }
      
      // Make sure discount progress bar is updated initially
      this.updateDiscountProgressBar();
    },
    
    /**
     * Update all UI components
     * @param {boolean} updateVariant - Whether to update the variant selection
     */
    updateUI: function(updateVariant = true) {
      this.updateSelectionOverview();
      
      // Update discount progress bar
      this.updateDiscountProgressBar();
      
      // Only update the variant if requested
      if (updateVariant) {
        VariantManager.updateVariantBySelection(this.state.allSelections, this.state.selectedGroups);
      }

      this.syncExternalState();
    },

    syncExternalState: function() {
      BundleManager.sync();
    },
    
    /**
     * Create the discount progress bar
     */
    createDiscountProgressBar: function() {
      const container = this.getContainer();
      if (!container || container.querySelector('.discount-progress-container')) return;

      const quantitySelectionRow = container.querySelector('.quantity-selection-row');
      if (!quantitySelectionRow) return;
      
      // Create progress bar container
      const progressContainer = DOMHelper.createElement('div', {
        className: 'discount-progress-container',
        attributes: {
          'aria-label': Config.translations.get('discountProgress')
        }
      });
      
      // Create title label for progress bar
      const progressTitle = DOMHelper.createElement('div', {
        className: 'discount-progress-title',
        text: Config.translations.get('rabattometerTitle')
      });
      
      // Create progress bar
      const progressBar = DOMHelper.createElement('div', {
        className: 'discount-progress-bar'
      });
      
      // Create progress fill
      const progressFill = DOMHelper.createElement('div', {
        className: 'discount-progress-fill'
      });
      
      // Create discount markers container
      const markersContainer = DOMHelper.createElement('div', {
        className: 'discount-markers'
      });
      
      // Create discount percentage labels container
      const percentagesContainer = DOMHelper.createElement('div', {
        className: 'discount-percentage-labels'
      });
      
      // Create discount value buttons container
      const buttonsContainer = DOMHelper.createElement('div', {
        className: 'discount-value-buttons' // Renamed container class
      });
      
      // Add markers, percentage labels, and buttons for each discount level
      this.state.discountLevels.forEach(level => {
        const jarCount = level.quantity * 3;
        const jarsLabel = Config.translations.get('jarsLabel');
        const buttonText = `${jarCount} ${jarsLabel}`;
        const percentageText = `-${level.discount}%`;
        
        // Create marker
        const marker = DOMHelper.createElement('div', {
          className: 'discount-marker',
          attributes: { 'data-quantity': level.quantity }
        });
        
        // Create percentage label
        const percentageLabel = DOMHelper.createElement('div', {
          className: 'discount-percentage-label',
          text: percentageText,
          attributes: { 'data-quantity': level.quantity }
        });
        
        // Create value BUTTON
        const valueButton = DOMHelper.createElement('button', {
          className: 'discount-value-button',
          text: buttonText,
          attributes: {
            'data-quantity': level.quantity,
            'type': 'button'
          }
        });
        
        // Add click listener directly to the button
        valueButton.addEventListener('click', () => {
          this.updateQuantitySelection(level.quantity);
        });
        
        markersContainer.appendChild(marker);
        percentagesContainer.appendChild(percentageLabel);
        buttonsContainer.appendChild(valueButton);
      });
      
      // Create dynamic hint element
      const discountHint = DOMHelper.createElement('div', {
        className: 'discount-hint'
      });
      
      // Assemble the progress bar
      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressTitle);
      progressContainer.appendChild(progressBar);
      progressContainer.appendChild(markersContainer);
      progressContainer.appendChild(percentagesContainer);
      progressContainer.appendChild(buttonsContainer);
      progressContainer.appendChild(discountHint);
      
      // Insert at the beginning of quantity selection row
      quantitySelectionRow.insertBefore(progressContainer, quantitySelectionRow.firstChild);
    },
    
    /**
     * Update the discount progress bar based on selected quantity
     */
    updateDiscountProgressBar: function() {
      const container = this.getContainer();
      if (!container) return;

      const selectedQuantity = this.state.selectedGroups;
      const progressFill = container.querySelector('.discount-progress-fill');
      const markers = container.querySelectorAll('.discount-marker');
      const percentageLabels = container.querySelectorAll('.discount-percentage-label');
      const valueButtons = container.querySelectorAll('.discount-value-button');
      const discountHint = container.querySelector('.discount-hint');
      
      if (!progressFill || !discountHint || markers.length === 0 || percentageLabels.length === 0 || valueButtons.length === 0) {
        return;
      }
      
      // Find the current discount level
      const currentLevel = this.state.discountLevels.find(level => level.quantity === selectedQuantity);
      
      // Handle case where level might not be found (e.g., initial state before selection)
      const effectiveQuantity = currentLevel ? selectedQuantity : 0;
      const discount = currentLevel ? currentLevel.discount : 0;
      
      // Calculate progress
      const maxQuantity = this.state.discountLevels.length;
      const maxDiscount = this.state.discountLevels[maxQuantity - 1].discount;
      const progress = maxDiscount > 0 ? (discount / maxDiscount) * 100 : 0;
      
      // Update progress fill width
      progressFill.style.width = `${progress}%`;
      
      // Update markers 
      markers.forEach(marker => {
        const markerQuantity = parseInt(marker.dataset.quantity);
        marker.classList.toggle('active', markerQuantity === effectiveQuantity);
        marker.classList.toggle('reached', markerQuantity <= effectiveQuantity && markerQuantity > 0);
      });
      
      // Update percentage labels
      percentageLabels.forEach(label => {
        const labelQuantity = parseInt(label.dataset.quantity);
        label.classList.toggle('active', labelQuantity === effectiveQuantity);
        label.classList.toggle('reached', labelQuantity <= effectiveQuantity && labelQuantity > 0);
      });
      
      // Update value buttons
      valueButtons.forEach(button => {
        const buttonQuantity = parseInt(button.dataset.quantity);
        button.classList.toggle('active', buttonQuantity === effectiveQuantity);
        // Buttons don't need a separate 'reached' style, active is enough
      });
      
      // Update dynamic hint text
      let hintText = '';
      if (effectiveQuantity < maxQuantity && effectiveQuantity > 0) {
        const nextLevel = this.state.discountLevels.find(level => level.quantity === effectiveQuantity + 1);
        if (nextLevel) {
          const jarsNeeded = nextLevel.quantity * 3;
          const nextDiscountHintTemplate = Config.translations.get('nextDiscountHint');
          hintText = nextDiscountHintTemplate
            .replace('{jarsNeeded}', jarsNeeded)
            .replace('{discount}', nextLevel.discount);
        }
      } else if (effectiveQuantity === maxQuantity) {
        hintText = Config.translations.get('maxDiscountHint');
      } else { // effectiveQuantity is 0 or invalid
          hintText = Config.translations.get('selectJarsHint');
      }
      discountHint.textContent = hintText;
    }
  };

  const AnySealPicker = {
    state: {
      selectedPacks: 1,
      discountLevels: [
        { quantity: 1, discount: 0 },
        { quantity: 2, discount: 5 },
        { quantity: 3, discount: 10 },
        { quantity: 4, discount: 15 }
      ]
    },

    getContainer: function() {
      const scope = getScopeRoot();
      return scope.querySelector('.ecoseal-variant-picker--anyseal');
    },

    getOverview: function() {
      const container = this.getContainer();
      return container ? container.querySelector('#anyseal-selection-overview') : null;
    },

    getStarterSku: function() {
      const starter = BundleManager.getAnySealStarterVariant();
      return starter && starter.sku ? starter.sku : '_ECOPRO1SET_ANYSEAL';
    },

    init: function() {
      const container = this.getContainer();
      if (!container) return;
      if (container.dataset.ecosealAnySealInitialized === 'true') {
        this.updateUI(false);
        return;
      }

      container.dataset.ecosealAnySealInitialized = 'true';
      this.createDiscountProgressBar();
      this.setupEventListeners();
      this.updateUI(false);
      BundleManager.bindGlobalEvents();
    },

    resetState: function(updateVariant = false) {
      this.state.selectedPacks = 1;
      this.updateUI(updateVariant);
    },

    updateQuantitySelection: function(quantity) {
      const safeQuantity = Math.max(1, Math.min(4, parseInt(quantity, 10) || 1));
      this.state.selectedPacks = safeQuantity;
      this.updateUI(true);
    },

    createPreview: function(imageUrl, altText) {
      const preview = DOMHelper.createElement('div', {
        className: 'selection-preview selection-preview--anyseal media-wrapper media-wrapper--small'
      });

      const img = DOMHelper.createElement('img', {
        is: 'lazy-image',
        attributes: {
          src: imageUrl,
          alt: altText,
          loading: 'lazy',
          decoding: 'async'
        },
        styles: {
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%'
        }
      });

      preview.setAttribute('loading', 'true');
      const markLoaded = () => preview.removeAttribute('loading');
      if (img.complete) {
        markLoaded();
      } else {
        img.addEventListener('load', () => (window.requestIdleCallback ? window.requestIdleCallback(markLoaded, { timeout: 150 }) : setTimeout(markLoaded)), { once: true });
        img.addEventListener('error', markLoaded, { once: true });
      }

      preview.appendChild(img);
      return preview;
    },

    createOverviewRow: function(options = {}) {
      const row = DOMHelper.createElement('div', {
        className: `trio-item non-selectable anyseal-overview-row ${options.className || ''}`.trim()
      });

      row.appendChild(this.createPreview(options.image, options.alt || 'AnySeal'));

      const textWrap = DOMHelper.createElement('div', {
        className: 'anyseal-overview-copy'
      });

      textWrap.appendChild(DOMHelper.createElement('div', {
        className: 'trio-description anyseal-overview-title',
        text: options.title || ''
      }));

      if (options.description) {
        textWrap.appendChild(DOMHelper.createElement('div', {
          className: 'anyseal-overview-subline',
          text: options.description
        }));
      }

      row.appendChild(textWrap);
      return row;
    },

    updateSelectionOverview: function() {
      const overview = this.getOverview();
      if (!overview) return;

      overview.innerHTML = '';

      overview.appendChild(this.createOverviewRow({
        className: 'anyseal-overview-row--starter',
        image: Config.assets.getAnySealStarterImage(),
        alt: Config.translations.get('anySealStarterTitle'),
        title: `1x ${Config.translations.get('ecosealpro')}`
      }));

      overview.appendChild(this.createOverviewRow({
        className: 'anyseal-overview-row--adapter',
        image: Config.assets.getAnySealAdapterImage(),
        alt: Config.translations.get('anySealAdapterTitle'),
        title: `1x ${Config.translations.get('anySealAdapterTitle')}`
      }));

      const totalLidCount = Math.max(1, this.state.selectedPacks) * 3;
      overview.appendChild(this.createOverviewRow({
        className: 'anyseal-overview-row--lids',
        image: Config.assets.getAnySealLidsImage(),
        alt: Config.translations.get('anySealLidsTitle'),
        title: `${totalLidCount}x ${Config.translations.get('anySealLidsTitle')}`
      }));
    },

    createDiscountProgressBar: function() {
      const container = this.getContainer();
      if (!container || container.querySelector('.discount-progress-container')) return;

      const quantitySelectionRow = container.querySelector('.quantity-selection-row');
      if (!quantitySelectionRow) return;

      const progressContainer = DOMHelper.createElement('div', {
        className: 'discount-progress-container',
        attributes: {
          'aria-label': Config.translations.get('discountProgress')
        }
      });

      const progressTitle = DOMHelper.createElement('div', {
        className: 'discount-progress-title',
        text: Config.translations.get('rabattometerTitle')
      });

      const progressBar = DOMHelper.createElement('div', {
        className: 'discount-progress-bar'
      });

      const progressFill = DOMHelper.createElement('div', {
        className: 'discount-progress-fill'
      });

      const markersContainer = DOMHelper.createElement('div', {
        className: 'discount-markers'
      });

      const percentagesContainer = DOMHelper.createElement('div', {
        className: 'discount-percentage-labels'
      });

      const buttonsContainer = DOMHelper.createElement('div', {
        className: 'discount-value-buttons'
      });

      this.state.discountLevels.forEach((level) => {
        const unitCount = level.quantity * 3;

        const marker = DOMHelper.createElement('div', {
          className: 'discount-marker',
          attributes: { 'data-quantity': level.quantity }
        });

        const percentageLabel = DOMHelper.createElement('div', {
          className: 'discount-percentage-label',
          text: `-${level.discount}%`,
          attributes: { 'data-quantity': level.quantity }
        });

        const valueButton = DOMHelper.createElement('button', {
          className: 'discount-value-button',
          text: `${unitCount} ${Config.translations.get('lidsLabel')}`,
          attributes: {
            'data-quantity': level.quantity,
            'type': 'button'
          }
        });

        valueButton.addEventListener('click', () => {
          this.updateQuantitySelection(level.quantity);
        });

        markersContainer.appendChild(marker);
        percentagesContainer.appendChild(percentageLabel);
        buttonsContainer.appendChild(valueButton);
      });

      const discountHint = DOMHelper.createElement('div', {
        className: 'discount-hint'
      });

      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressTitle);
      progressContainer.appendChild(progressBar);
      progressContainer.appendChild(markersContainer);
      progressContainer.appendChild(percentagesContainer);
      progressContainer.appendChild(buttonsContainer);
      progressContainer.appendChild(discountHint);

      quantitySelectionRow.insertBefore(progressContainer, quantitySelectionRow.firstChild);
    },

    updateDiscountProgressBar: function() {
      const container = this.getContainer();
      if (!container) return;

      const selectedQuantity = this.state.selectedPacks;
      const progressFill = container.querySelector('.discount-progress-fill');
      const markers = container.querySelectorAll('.discount-marker');
      const percentageLabels = container.querySelectorAll('.discount-percentage-label');
      const valueButtons = container.querySelectorAll('.discount-value-button');
      const discountHint = container.querySelector('.discount-hint');

      if (!progressFill || !discountHint || !markers.length || !percentageLabels.length || !valueButtons.length) return;

      const currentLevel = this.state.discountLevels.find((level) => level.quantity === selectedQuantity);
      const effectiveQuantity = currentLevel ? selectedQuantity : 0;
      const discount = currentLevel ? currentLevel.discount : 0;
      const maxQuantity = this.state.discountLevels.length;
      const maxDiscount = this.state.discountLevels[maxQuantity - 1].discount;
      const progress = maxDiscount > 0 ? (discount / maxDiscount) * 100 : 0;

      progressFill.style.width = `${progress}%`;

      markers.forEach((marker) => {
        const markerQuantity = parseInt(marker.dataset.quantity, 10);
        marker.classList.toggle('active', markerQuantity === effectiveQuantity);
        marker.classList.toggle('reached', markerQuantity <= effectiveQuantity && markerQuantity > 0);
      });

      percentageLabels.forEach((label) => {
        const labelQuantity = parseInt(label.dataset.quantity, 10);
        label.classList.toggle('active', labelQuantity === effectiveQuantity);
        label.classList.toggle('reached', labelQuantity <= effectiveQuantity && labelQuantity > 0);
      });

      valueButtons.forEach((button) => {
        const buttonQuantity = parseInt(button.dataset.quantity, 10);
        button.classList.toggle('active', buttonQuantity === effectiveQuantity);
      });

      let hintText = '';
      if (effectiveQuantity < maxQuantity && effectiveQuantity > 0) {
        const nextLevel = this.state.discountLevels.find((level) => level.quantity === effectiveQuantity + 1);
        if (nextLevel) {
          const nextDiscountHintTemplate = Config.translations.get('nextLidDiscountHint');
          hintText = nextDiscountHintTemplate
            .replace('{lidsNeeded}', nextLevel.quantity * 3)
            .replace('{discount}', nextLevel.discount);
        }
      } else if (effectiveQuantity === maxQuantity) {
        hintText = Config.translations.get('maxDiscountHint');
      } else {
        hintText = Config.translations.get('selectLidsHint');
      }

      discountHint.textContent = hintText;
    },

    updateUI: function(updateVariant = true) {
      this.updateSelectionOverview();
      this.updateDiscountProgressBar();

      if (updateVariant) {
        VariantManager.updateVariantBySku(this.getStarterSku());
      }

      this.syncExternalState();
    },

    syncExternalState: function() {
      BundleManager.sync();
    },

    setupEventListeners: function() {
      const container = this.getContainer();
      if (!container || container.dataset.ecosealAnySealEventsBound === 'true') return;
      container.dataset.ecosealAnySealEventsBound = 'true';
      this.updateDiscountProgressBar();
    }
  };
  
  // Expose globally for merged picker access
  window.EcoSealPicker = EcoSealPicker;
  window.EcoSealAnySealPicker = AnySealPicker;
  window.JarData = JarData;
  window.VariantManager = VariantManager;
  window.EcoSealBundleManager = BundleManager;
  
  // Initialize the application once DOM is loaded only if not in merged mode
  document.addEventListener('DOMContentLoaded', function() {
    // Only auto-initialize if we're not in a merged picker (standalone mode)
    if (!document.querySelector('.ecoseal-merged-picker')) {
      // Initialize the variant picker
      EcoSealPicker.init();
    }
  });

  /* -------------------------------------------------------------------------- */
  /* Fallback initialisation for dynamically-injected content (quick-view modal) */
  /* -------------------------------------------------------------------------- */

  (function() {
    const initialiseEcoSeal = function() {
      // Only initialise if a picker exists in the current DOM scope
      if (!document.querySelector('.ecoseal-variant-picker')) return;
      
      // Only auto-initialize if we're not in a merged picker (standalone mode)
      if (!document.querySelector('.ecoseal-merged-picker')) {
        EcoSealPicker.init();
      }
    };

    // If the DOM is already parsed (typical for quick-view injections) run immediately
    if (document.readyState !== 'loading') {
      initialiseEcoSeal();
    }

    // Re-initialise whenever a quick-view modal finishes loading its content
    document.addEventListener('quickview:loaded', initialiseEcoSeal);
  })();
})(); 


/**
 * EcoSealPro Merged Variant Picker Manager
 * Coordinates between the world chooser, the Seal-it picker flow and the AnySeal picker flow.
 */

(function() {
  'use strict';

  const getScopeRoot = function() {
    return (
      document.querySelector('quick-view-drawer details[open]') ||
      document.querySelector('quick-view-drawer details.menu-opening') ||
      document.querySelector('.quick-view--open') ||
      document.querySelector('.quick-view--opening') ||
      document
    );
  };

  const getMergedContainer = function(root) {
    const scope = root || getScopeRoot();
    return scope.querySelector('.ecoseal-merged-picker');
  };

  window.ecosealMergedPicker = {
    _initialisedContainers: null,
    _carouselAssetsPromise: null,
    _pendingCustomInitTimer: null,
    _pendingCustomInitTries: 0,
    _panelTransitionTimer: null,

    init: function() {
      const root = getScopeRoot();
      const container = getMergedContainer(root);
      if (!container) return;

      const ctx = { root, container };

      if (!this._initialisedContainers) {
        this._initialisedContainers = new WeakSet();
      }

      if (this._initialisedContainers.has(container)) {
        this.syncState(ctx);
        return;
      }

      this._initialisedContainers.add(container);
      this.setupWorldTabHint(ctx);
      this.setupSwitching(ctx);
      this.setupTabMetricSync(ctx);
      this.intelligentInit(ctx);
    },

    getContext: function() {
      const root = getScopeRoot();
      const container = getMergedContainer(root);
      return { root, container };
    },

    getUrlParameter: function(name) {
      name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
      const results = regex.exec(location.search);
      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    },

    getCurrentVariant: function(ctx) {
      const root = (ctx && ctx.root) ? ctx.root : this.getContext().root;
      const scope = root || document;
      const urlVariantId = this.getUrlParameter('variant');

      if (urlVariantId) {
        const optByUrl = scope.querySelector(`select[id^="Option-"][name^="options["] option[data-variant-id="${urlVariantId}"]`);
        if (optByUrl) {
          return { id: Number(urlVariantId), sku: optByUrl.dataset.variantSku || null };
        }
      }

      const selectElement = scope.querySelector('select[id^="Option-"][name^="options["]');
      if (selectElement && selectElement.selectedIndex >= 0) {
        const currentSelectedOption = selectElement.options[selectElement.selectedIndex];
        if (currentSelectedOption && currentSelectedOption.dataset.variantId) {
          return {
            id: Number(currentSelectedOption.dataset.variantId),
            sku: currentSelectedOption.dataset.variantSku || null
          };
        }
      }

      const idInput = scope.querySelector('input[name="id"]');
      if (idInput && idInput.value) {
        return { id: Number(idInput.value) };
      }

      return null;
    },

    isAnySealVariant: function(variant) {
      const safeSku = variant && variant.sku ? String(variant.sku).trim() : '';
      const config = window.ecosealCartConfig || {};
      const anySealSku = config.anySealStarterVariant && config.anySealStarterVariant.sku
        ? String(config.anySealStarterVariant.sku).trim()
        : '_ECOPRO1SET_ANYSEAL';
      return !!safeSku && safeSku === anySealSku;
    },

    findFixedSetByVariant: function(variant) {
      if (!variant || !variant.sku) return null;

      if (Array.isArray(window.ecosealFixedSetSkus) && window.ecosealFixedSetSkus.indexOf(variant.sku) !== -1) {
        return { sku: variant.sku };
      }

      if (window.EcoSealCarouselPicker && window.EcoSealCarouselPicker.Config && window.EcoSealCarouselPicker.Config.fixedSets) {
        return window.EcoSealCarouselPicker.Config.fixedSets.find((set) => set.sku === variant.sku) || null;
      }

      return null;
    },

    intelligentInit: function(ctx) {
      const context = ctx || this.getContext();
      if (!context.container) return;

      const explicitVariantId = this.getUrlParameter('variant');
      const currentVariant = this.getCurrentVariant(context);

      if (!explicitVariantId) {
        this.showWorldChooser(context, { resetVariant: true, resetSealItView: true });
        return;
      }

      if (this.isAnySealVariant(currentVariant)) {
        this.showAnySeal(context, { syncVariant: false });
        return;
      }

      const fixedSet = this.findFixedSetByVariant(currentVariant);
      if (fixedSet) {
        this.showSealItCarousel(context, {
          preselectedSet: fixedSet,
          skipVariantUpdate: true
        });
        return;
      }

      this.showSealItCustom(context, { syncVariant: false });
    },

    getPanel: function(container, panelName) {
      return container ? container.querySelector(`[data-world-panel="${panelName}"]`) : null;
    },

    syncTabMetrics: function(ctx) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      const tabs = Array.from(container.querySelectorAll('.ecoseal-world-tab[data-world-choice]'));
      const tabsRow = container.querySelector('.ecoseal-world-tabs');
      const tabMetrics = [];

      tabs.forEach((tab) => {
        const title = tab.querySelector('.ecoseal-world-tab__title');
        if (!title) return;

        const styles = window.getComputedStyle(tab);
        const titleStyles = window.getComputedStyle(title);
        const horizontalPadding =
          parseFloat(styles.paddingLeft || '0') +
          parseFloat(styles.paddingRight || '0') +
          parseFloat(styles.borderLeftWidth || '0') +
          parseFloat(styles.borderRightWidth || '0');

        const probe = document.createElement('span');
        probe.textContent = title.textContent || '';
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        probe.style.whiteSpace = 'nowrap';
        probe.style.pointerEvents = 'none';
        probe.style.fontFamily = titleStyles.fontFamily;
        probe.style.fontSize = titleStyles.fontSize;
        probe.style.fontWeight = titleStyles.fontWeight;
        probe.style.letterSpacing = titleStyles.letterSpacing;
        probe.style.lineHeight = titleStyles.lineHeight;
        document.body.appendChild(probe);

        const textWidth = probe.getBoundingClientRect().width;
        probe.remove();

        const width = Math.ceil(textWidth + horizontalPadding + 2);
        tab.style.setProperty('--ecoseal-tab-closed-width', `${width}px`);
        tabMetrics.push({ tab, closedWidth: width });
      });

      if (!tabsRow || !tabMetrics.length) return;

      const rowStyles = window.getComputedStyle(tabsRow);
      const gap = parseFloat(rowStyles.columnGap || rowStyles.gap || '0') || 0;
      const rowWidth = tabsRow.getBoundingClientRect().width;

      tabMetrics.forEach(({ tab }) => {
        const otherTabsWidth = tabMetrics
          .filter((metric) => metric.tab !== tab)
          .reduce((sum, metric) => sum + metric.closedWidth, 0);
        const activeWidth = Math.max(120, rowWidth - otherTabsWidth - (gap * (tabMetrics.length - 1)));
        const probe = tab.cloneNode(true);

        probe.classList.add('is-active');
        probe.style.position = 'absolute';
        probe.style.visibility = 'hidden';
        probe.style.pointerEvents = 'none';
        probe.style.left = '0';
        probe.style.top = '0';
        probe.style.width = `${activeWidth}px`;
        probe.style.height = 'auto';
        probe.style.flex = 'none';
        probe.style.transform = 'none';
        probe.style.animation = 'none';
        probe.style.transition = 'none';
        probe.style.zIndex = '-1';
        tabsRow.appendChild(probe);

        const activeHeight = Math.ceil(probe.getBoundingClientRect().height);
        probe.remove();

        if (activeHeight > 0) {
          tab.style.setProperty('--ecoseal-tab-active-height', `${activeHeight}px`);
        }
      });
    },

    syncTabNudgeLift: function(ctx) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      const activeTab = container.querySelector('.ecoseal-world-tab.is-active');
      if (!activeTab) return;

      const rootStyles = window.getComputedStyle(container);
      const tuck = parseFloat(rootStyles.getPropertyValue('--ecoseal-folder-tab-tuck')) || 10;
      const overshoot = window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 4 : 6;

      container.querySelectorAll('.ecoseal-world-tab[data-world-choice]').forEach((tab) => {
        if (tab === activeTab) {
          tab.style.removeProperty('--ecoseal-tab-nudge-peak');
          return;
        }

        const targetTranslate = activeTab.offsetTop - tab.offsetTop - overshoot;
        const minimumLift = tuck - 16;
        const peakTranslate = Math.min(minimumLift, targetTranslate);

        tab.style.setProperty('--ecoseal-tab-nudge-peak', `${Math.round(peakTranslate * 10) / 10}px`);
      });
    },

    setupTabMetricSync: function(ctx) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container || container.dataset.ecosealTabMetricsBound === 'true') return;

      container.dataset.ecosealTabMetricsBound = 'true';

      const sync = () => {
        const latestContext = this.getContext();
        this.syncTabMetrics(latestContext);
        this.syncTabNudgeLift(latestContext);
      };
      requestAnimationFrame(sync);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(sync).catch(() => {});
      }

      let resizeTimer = null;
      window.addEventListener('resize', () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(sync, 120);
      }, { passive: true });
    },

    markWorldTabHintSeen: function(container) {
      if (container) {
        container.classList.add('has-switched-world');
      }
    },

    setupWorldTabHint: function(ctx) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      container.classList.remove('has-switched-world');
    },

    updateWorldTabs: function(ctx, panelName) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      this.syncTabMetrics(context);

      container.querySelectorAll('.ecoseal-world-tab[data-world-choice]').forEach((tab) => {
        const isActive = tab.getAttribute('data-world-choice') === panelName;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = 0;
      });

      requestAnimationFrame(() => {
        this.syncTabNudgeLift(this.getContext());
      });

      if (this._tabNudgeLiftTimer) {
        clearTimeout(this._tabNudgeLiftTimer);
      }
      this._tabNudgeLiftTimer = setTimeout(() => {
        this.syncTabNudgeLift(this.getContext());
      }, 460);
    },

    setActivePanel: function(ctx, panelName, options = {}) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      const stage = container.querySelector('.ecoseal-world-stage');
      const previousPanel = container.querySelector('[data-world-panel].is-active');
      const isChangingPanel = !!(previousPanel && previousPanel.dataset.worldPanel !== panelName);
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const animatePanel = options.animatePanel !== false && isChangingPanel && !reduceMotion && stage;

      if (this._panelTransitionTimer) {
        clearTimeout(this._panelTransitionTimer);
        this._panelTransitionTimer = null;
      }

      container.querySelectorAll('[data-world-panel]').forEach((panel) => {
        panel.classList.remove('is-folding-in', 'is-folding-out');
      });

      if (stage) {
        stage.classList.remove('is-transitioning');
        stage.style.minHeight = '';
        stage.style.height = '';
      }

      let stageStartHeight = 0;
      if (animatePanel) {
        stageStartHeight = stage.getBoundingClientRect().height;
        stage.style.height = `${stageStartHeight}px`;
        stage.classList.add('is-transitioning');
        previousPanel.classList.add('is-folding-out');
      }

      container.dataset.activeWorld = panelName;
      if (stage) {
        stage.dataset.activePanel = panelName;
      }
      this.updateWorldTabs(context, panelName);

      container.querySelectorAll('[data-world-panel]').forEach((panel) => {
        const isActive = panel.dataset.worldPanel === panelName;
        const isPreviousAnimated = animatePanel && panel === previousPanel;
        panel.hidden = !(isActive || isPreviousAnimated);
        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('is-folding-in', !!(animatePanel && isActive));
        if (!isPreviousAnimated) {
          panel.classList.remove('is-folding-out');
        }
      });

      if (animatePanel) {
        requestAnimationFrame(() => {
          stage.style.height = `${stageStartHeight}px`;
          stage.offsetHeight;
          const stageTargetHeight = stage.scrollHeight;
          stage.style.height = `${stageTargetHeight}px`;
        });

        this._panelTransitionTimer = setTimeout(() => {
          previousPanel.hidden = true;
          previousPanel.classList.remove('is-folding-out');
          container.querySelectorAll('[data-world-panel].is-folding-in').forEach((panel) => {
            panel.classList.remove('is-folding-in');
          });
          stage.classList.remove('is-transitioning');
          stage.style.height = '';
          this._panelTransitionTimer = null;
        }, 520);
      }
    },

    syncState: function(ctx) {
      const context = ctx || this.getContext();
      if (window.EcoSealBundleManager && typeof window.EcoSealBundleManager.sync === 'function') {
        window.EcoSealBundleManager.sync(context.root);
      }
    },

    getDefaultVariantSku: function(ctx) {
      const context = ctx || this.getContext();
      const config = window.ecosealCartConfig || {};
      const anySealStarter = config.anySealStarterVariant && config.anySealStarterVariant.sku
        ? String(config.anySealStarterVariant.sku).trim()
        : '';
      if (anySealStarter) return anySealStarter;

      const root = (context && context.root) ? context.root : this.getContext().root;
      const selectElement = root && window.VariantManager && typeof window.VariantManager.getSelectElement === 'function'
        ? window.VariantManager.getSelectElement()
        : null;

      if (!selectElement || !selectElement.options || !selectElement.options.length) return '';
      const firstOption = selectElement.options[0];
      return firstOption && firstOption.dataset ? String(firstOption.dataset.variantSku || '').trim() : '';
    },

    activateDefaultVariant: function(ctx) {
      const defaultSku = this.getDefaultVariantSku(ctx);
      if (!defaultSku) return;

      if (window.VariantManager && typeof window.VariantManager.updateVariantBySku === 'function') {
        window.VariantManager.updateVariantBySku(defaultSku);
      }
    },

    showWorldChooser: function(ctx, options = {}) {
      const context = ctx || this.getContext();
      if (!context.container) return;

      if (options.resetSealItView !== false) {
        context.container.dataset.sealitView = 'custom';
      }

      if (options.resetVariant === true) {
        this.activateDefaultVariant(context);
      }

      this.setActivePanel(context, 'chooser');
      this.syncState(context);
    },

    showSealItCustom: function(ctx, options = {}) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      this.initializeCustom(context);
      container.dataset.sealitView = 'custom';
      this.setActivePanel(context, 'sealit', {
        animatePanel: options.animatePanel
      });

      const carouselElement = container.querySelector('#carousel-picker');
      const customElement = container.querySelector('#custom-picker');
      const showCustomLink = container.querySelector('#show-custom-link');
      if (carouselElement) carouselElement.style.display = 'none';
      if (customElement) customElement.style.display = 'block';
      if (showCustomLink) showCustomLink.hidden = true;

      if (window.EcoSealPicker && typeof window.EcoSealPicker.updateUI === 'function') {
        window.EcoSealPicker.updateUI(options.syncVariant !== false);
      }

      this.syncState(context);
    },

    rememberPopularSetsReturnState: function(ctx) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      const activeWorld = container.dataset.activeWorld || 'chooser';
      const sealitView = container.dataset.sealitView || 'custom';
      if (activeWorld === 'sealit' && sealitView === 'carousel') return;

      container.dataset.popularSetsReturnWorld = activeWorld;
      container.dataset.popularSetsReturnSealitView = sealitView;
    },

    returnFromPopularSets: function(ctx) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      const returnWorld = container.dataset.popularSetsReturnWorld || 'sealit';
      const returnSealitView = container.dataset.popularSetsReturnSealitView || 'custom';
      delete container.dataset.popularSetsReturnWorld;
      delete container.dataset.popularSetsReturnSealitView;

      if (returnWorld === 'anyseal') {
        this.showAnySeal(context, { syncVariant: true });
        return;
      }

      if (returnWorld === 'chooser') {
        this.showWorldChooser(context, { resetVariant: false, resetSealItView: false });
        return;
      }

      if (returnWorld === 'sealit' && returnSealitView === 'carousel') {
        this.showSealItCarousel(context, { skipReset: true });
        return;
      }

      this.showSealItCustom(context, { syncVariant: true });
    },

    showSealItCarousel: function(ctx, options = {}) {
      const context = ctx || this.getContext();
      const { container } = context;
      if (!container) return;

      if (options.rememberReturnState === true) {
        this.rememberPopularSetsReturnState(context);
      }

      const revealCarousel = () => {
        const carouselElement = container.querySelector('#carousel-picker');
        const customElement = container.querySelector('#custom-picker');
        const showCustomLink = container.querySelector('#show-custom-link');
        const currentVariant = this.getCurrentVariant(context);
        const currentFixedSet = this.findFixedSetByVariant(currentVariant);
        const targetSet = options.preselectedSet || currentFixedSet || null;
        container.dataset.sealitView = 'carousel';
        this.setActivePanel(context, 'sealit', {
          animatePanel: options.animatePanel
        });
        if (customElement) customElement.style.display = 'none';
        if (carouselElement) carouselElement.style.display = 'block';
        if (showCustomLink) showCustomLink.hidden = false;

        if (targetSet && window.EcoSealCarouselPicker && typeof window.EcoSealCarouselPicker.selectSetBySku === 'function') {
          const skipVariantUpdate = options.skipVariantUpdate === true && !!options.preselectedSet;
          window.EcoSealCarouselPicker.selectSetBySku(targetSet.sku, skipVariantUpdate);
        } else if (window.EcoSealCarouselPicker && typeof window.EcoSealCarouselPicker.getDefaultSet === 'function' && !options.skipReset) {
          const defaultSet = window.EcoSealCarouselPicker.getDefaultSet();
          if (defaultSet && typeof window.EcoSealCarouselPicker.selectSet === 'function') {
            window.EcoSealCarouselPicker.selectSet(defaultSet, false);
          }
        }

        this.syncState(context);
      };

      this.ensureCarouselAssetsLoaded()
        .then(() => {
          const freshContext = this.getContext();
          this.initializeCarousel(freshContext);
          revealCarousel();
        })
        .catch(() => {
          this.showSealItCustom(context, { syncVariant: true });
        });
    },

    showAnySeal: function(ctx, options = {}) {
      const context = ctx || this.getContext();
      if (!context.container) return;

      this.initializeAnySeal(context);
      this.setActivePanel(context, 'anyseal', {
        animatePanel: options.animatePanel
      });

      if (window.EcoSealAnySealPicker && typeof window.EcoSealAnySealPicker.updateUI === 'function') {
        window.EcoSealAnySealPicker.updateUI(options.syncVariant !== false);
      }

      this.syncState(context);
    },

    initializeCarousel: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;

      const carouselElement = container.querySelector('#carousel-picker');
      if (carouselElement && carouselElement.dataset.ecosealMergedCarouselInit === 'true') {
        return;
      }

      if (!window.EcoSealCarouselPicker) return;

      const hasSets = !!(
        window.EcoSealCarouselPicker &&
        window.EcoSealCarouselPicker.Config &&
        Array.isArray(window.EcoSealCarouselPicker.Config.fixedSets) &&
        window.EcoSealCarouselPicker.Config.fixedSets.length > 0
      );

      if (typeof window.EcoSealCarouselPicker.init === 'function' && hasSets) {
        window.EcoSealCarouselPicker.init();
        if (carouselElement) {
          carouselElement.dataset.ecosealMergedCarouselInit = 'true';
        }
      } else if (carouselElement) {
        carouselElement.style.display = 'none';
      }
    },

    initializeCustom: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;

      const customElement = container.querySelector('#custom-picker');
      if (customElement && customElement.dataset.ecosealMergedCustomInit === 'true') {
        return;
      }

      if (window.EcoSealPicker && typeof window.EcoSealPicker.init === 'function') {
        window.EcoSealPicker.init();
        if (customElement) {
          customElement.dataset.ecosealMergedCustomInit = 'true';
        }
        this._pendingCustomInitTries = 0;
        if (this._pendingCustomInitTimer) {
          clearTimeout(this._pendingCustomInitTimer);
          this._pendingCustomInitTimer = null;
        }
      } else {
        if (this._pendingCustomInitTimer) return;
        if (this._pendingCustomInitTries >= 40) return;
        this._pendingCustomInitTries += 1;
        this._pendingCustomInitTimer = setTimeout(() => {
          this._pendingCustomInitTimer = null;
          this.initializeCustom();
        }, 50);
      }
    },

    initializeAnySeal: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container) return;

      const anySealElement = container.querySelector('#anyseal-picker');
      if (anySealElement && anySealElement.dataset.ecosealMergedAnysealInit === 'true') {
        return;
      }

      if (window.EcoSealAnySealPicker && typeof window.EcoSealAnySealPicker.init === 'function') {
        window.EcoSealAnySealPicker.init();
        if (anySealElement) {
          anySealElement.dataset.ecosealMergedAnysealInit = 'true';
        }
      }
    },

    ensureCarouselAssetsLoaded: function() {
      if (this._carouselAssetsPromise) return this._carouselAssetsPromise;
      if (window.EcoSealCarouselPicker) {
        this._carouselAssetsPromise = Promise.resolve();
        return this._carouselAssetsPromise;
      }

      const assets = window.ecosealCarouselAssets || {};
      const jsUrl = assets.js;
      const cssUrl = assets.css;

      this._carouselAssetsPromise = new Promise((resolve, reject) => {
        if (!jsUrl) {
          reject(new Error('Missing ecosealCarouselAssets.js'));
          return;
        }

        if (cssUrl) {
          const existingCss = document.querySelector(`link[rel="stylesheet"][href="${cssUrl}"]`);
          if (!existingCss) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssUrl;
            document.head.appendChild(link);
          }
        }

        const existingScript = document.querySelector(`script[src="${jsUrl}"]`);
        if (existingScript) {
          if (window.EcoSealCarouselPicker) {
            resolve();
            return;
          }
          existingScript.addEventListener('load', () => window.EcoSealCarouselPicker ? resolve() : reject(new Error('Carousel script loaded but global missing')));
          existingScript.addEventListener('error', () => reject(new Error('Carousel script failed to load')));
          return;
        }

        const script = document.createElement('script');
        script.src = jsUrl;
        script.defer = true;
        script.onload = () => window.EcoSealCarouselPicker ? resolve() : reject(new Error('Carousel script loaded but global missing'));
        script.onerror = () => reject(new Error('Carousel script failed to load'));
        document.head.appendChild(script);
      });

      return this._carouselAssetsPromise;
    },

    setupSwitching: function(ctx) {
      const container = (ctx && ctx.container) ? ctx.container : this.getContext().container;
      if (!container || container.dataset.ecosealMergedEventsBound === 'true') return;
      container.dataset.ecosealMergedEventsBound = 'true';

      container.addEventListener('click', (event) => {
        const worldChoice = event.target && event.target.closest ? event.target.closest('[data-world-choice]') : null;
        if (worldChoice) {
          event.preventDefault();
          const world = worldChoice.getAttribute('data-world-choice');
          const isTabSwitch = worldChoice.classList.contains('ecoseal-world-tab');
          const currentWorld = container.dataset.activeWorld;
          const isWorldSwitch = (currentWorld === 'anyseal' || currentWorld === 'sealit') &&
            (world === 'anyseal' || world === 'sealit') &&
            currentWorld !== world;

          if (isWorldSwitch) {
            this.markWorldTabHintSeen(container);
          }
          const switchOptions = {
            syncVariant: true,
            animatePanel: !isTabSwitch
          };

          if (world === 'anyseal') {
            this.showAnySeal(this.getContext(), switchOptions);
            return;
          }

          if (world === 'sealit') {
            this.showSealItCustom(this.getContext(), switchOptions);
          }
          return;
        }

        const showCarouselLink = event.target && event.target.closest ? event.target.closest('#show-carousel-link, [data-popular-sets-link]') : null;
        if (showCarouselLink) {
          event.preventDefault();
          this.showSealItCarousel(this.getContext(), { rememberReturnState: true });
          return;
        }

        const backToCustomButton = event.target && event.target.closest ? event.target.closest('#back-to-custom, #show-custom-link') : null;
        if (backToCustomButton) {
          event.preventDefault();
          this.returnFromPopularSets(this.getContext());
        }
      });
    }
  };

  document.addEventListener('DOMContentLoaded', function() {
    window.ecosealMergedPicker.init();
  });

  (function() {
    const initialiseMergedPicker = function() {
      if (!document.querySelector('.ecoseal-merged-picker')) return;
      window.ecosealMergedPicker.init();
    };

    if (document.readyState !== 'loading') {
      initialiseMergedPicker();
    }

    document.addEventListener('quickview:loaded', initialiseMergedPicker);
    document.addEventListener('product-info:loaded', initialiseMergedPicker);
  })();
})();
